// @ts-nocheck
import {useState, useEffect} from 'react'
import {useSearchParams, useParams, Link} from 'react-router-dom'
// PageTitle removed - using inline header instead
import {
  getSyncedListings,
  updateSyncedListing,
  aiSyncedListingDescription,
  deleteSyncedListing,
  FacebookSyncedListing,
  getFacebookProfiles,
  FacebookProfile,
  getMyListings,
  retryListing,
  deleteMyListing,
  MyListing,
  syncListingMessages,
  unmarkThreadReplied
} from './core/_requests'
import FacebookProfilesSync from './FacebookProfilesSync'
import {IsraelCitySelector} from './IsraelCitySelector'
import {toCdnUrl} from '../../utils/cdnUrl'
import './marketplace.scss'
import './components/marketplace-v2.scss'
import {ListingCard, ListingGrid, EmptyState, FilterBar, StatsBar, TabNavigation} from './components'

// Helper function to get condition label
function getConditionLabel(condition?: string): { label: string, color: string } {
  switch (condition?.toUpperCase()) {
    case 'NEW': return { label: 'חדש', color: '#198754' }
    case 'USED_LIKE_NEW': return { label: 'כמו חדש', color: '#20c997' }
    case 'USED_GOOD': return { label: 'מצב טוב', color: '#0d6efd' }
    case 'USED_FAIR': return { label: 'מצב סביר', color: '#ffc107' }
    default: return { label: '', color: '' }
  }
}

// Synced Listing Card Component
function SyncedListingCard({
  listing,
  onEdit,
  onShowDetails,
  onDelete
}: {
  listing: FacebookSyncedListing
  onEdit: () => void
  onShowDetails: () => void
  onDelete: () => void
}) {
  const getStatusBadge = () => {
    if (listing.is_sold) return { label: 'נמכר', color: 'dark' }
    if (!listing.is_live) return { label: 'לא פעיל', color: 'secondary' }
    if (listing.listing_is_rejected) return { label: 'נדחה', color: 'danger' }
    if (listing.is_pending) return { label: 'ממתין', color: 'warning' }
    return { label: 'פעיל', color: 'success' }
  }

  const status = getStatusBadge()
  const conditionInfo = getConditionLabel(listing.condition)

  // Calculate total interactions
  const totalMessages = listing.messages_count || listing.conversations?.length || 0
  const totalComments = listing.comments_count || listing.comments?.length || 0

  return (
    <div className="listing-card">
      <div className="listing-image">
        {listing.primary_photo_url ? (
          <img src={toCdnUrl(listing.primary_photo_url)} alt={listing.title} />
        ) : (
          <div className="no-image">
            <i className="bi bi-image"></i>
          </div>
        )}
        <span className={`status-badge badge-${status.color}`}>
          {status.label}
        </span>
        <span className="synced-badge" style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          background: '#1877f2',
          color: 'white',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '11px'
        }}>
          <i className="bi bi-facebook me-1"></i>
          מסונכרן
        </span>
        {/* Condition badge */}
        {conditionInfo.label && (
          <span style={{
            position: 'absolute',
            bottom: '8px',
            left: '8px',
            background: conditionInfo.color,
            color: 'white',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 500
          }}>
            {conditionInfo.label}
          </span>
        )}
      </div>
      <div className="listing-content">
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
          <div className="listing-type-badge">{listing.listing_type || 'פריט'}</div>
          {listing.brand && (
            <span style={{
              background: '#e9ecef',
              color: '#495057',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '11px'
            }}>{listing.brand}</span>
          )}
        </div>
        <h5 className="listing-title">{listing.title}</h5>
        <div className="listing-price">
          {listing.formatted_price || `₪${listing.price?.toLocaleString()}`}
          {listing.is_negotiable && (
            <span style={{ fontSize: '11px', color: '#6c757d', marginRight: '4px' }}>
              (מחיר גמיש)
            </span>
          )}
        </div>

        {/* Stats row */}
        <div className="listing-meta" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <span title="צפיות">
            <i className="bi bi-eye"></i>
            {listing.views_count || 0}
          </span>
          <span title="הודעות">
            <i className="bi bi-chat-dots"></i>
            {totalMessages}
          </span>
          <span title="תגובות">
            <i className="bi bi-chat-left-text"></i>
            {totalComments}
          </span>
          {(listing.saves_count || 0) > 0 && (
            <span title="שמירות">
              <i className="bi bi-bookmark"></i>
              {listing.saves_count}
            </span>
          )}
          {(listing.shares_count || 0) > 0 && (
            <span title="שיתופים">
              <i className="bi bi-share"></i>
              {listing.shares_count}
            </span>
          )}
        </div>

        {/* Date and location */}
        <div className="listing-meta" style={{ marginTop: '4px', fontSize: '11px', color: '#a1a5b7' }}>
          <span>
            <i className="bi bi-calendar3"></i>
            {listing.facebook_creation_time
              ? new Date(listing.facebook_creation_time).toLocaleDateString('he-IL')
              : '-'}
          </span>
          {listing.location && (
            <span>
              <i className="bi bi-geo-alt"></i>
              {listing.location}
            </span>
          )}
        </div>

        {/* Cross-post groups indicator */}
        {listing.available_crosspost_groups && listing.available_crosspost_groups.length > 0 && (
          <div style={{ marginTop: '6px', fontSize: '11px', color: '#6c757d' }}>
            <i className="bi bi-people me-1"></i>
            {listing.available_crosspost_groups.filter(g => g.is_posted).length}/
            {listing.available_crosspost_groups.length} קבוצות
          </div>
        )}

        {/* Auto-reply indicator */}
        {listing.auto_reply_message && (
          <div style={{
            marginTop: '6px',
            fontSize: '11px',
            color: '#198754',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <i className="bi bi-reply"></i>
            תשובה אוטומטית מוגדרת
          </div>
        )}
      </div>
      <div className="listing-actions">
        <button className="btn-edit" onClick={onShowDetails} title="פרטים נוספים" style={{ background: '#6f42c1', color: 'white' }}>
          <i className="bi bi-info-circle"></i>
        </button>
        <a
          href={listing.share_uri}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-edit"
          title="צפה בפייסבוק"
        >
          <i className="bi bi-box-arrow-up-right"></i>
        </a>
        <button className="btn-edit" onClick={onEdit} title="ערוך בפייסבוק" style={{ background: '#1877f2', color: 'white' }}>
          <i className="bi bi-pencil"></i>
        </button>
        <button className="btn-edit" onClick={onDelete} title="מחק מפייסבוק" style={{ background: '#dc3545', color: 'white' }}>
          <i className="bi bi-trash"></i>
        </button>
      </div>
    </div>
  )
}

// Empty State Component for synced listings
function EmptySyncedState() {
  return (
    <div className="empty-state" dir="rtl">
      <div className="empty-icon">
        <i className="bi bi-facebook"></i>
      </div>
      <h3>אין מודעות מסונכרנות</h3>
      <p>סנכרן את הפרופיל שלך למעלה כדי לראות את המודעות מפייסבוק</p>
    </div>
  )
}

// Helper to detect listing type category from Facebook's listing_inventory_type
// Facebook's own category ids, as sent when we publish (see the correctCategories map
// in api/routes/marketplace.js) and as they come back on sync.
const FB_CATEGORY_IDS: Record<string, 'item' | 'vehicle' | 'rental' | 'home_sale'> = {
  '807311116002614': 'vehicle',
  '821056594720130': 'home_sale',
  '1468271819871448': 'rental',
  '895487550471874': 'home_sale',
}

// Resolve what KIND of listing this is, so the edit form shows the right fields.
//
// This must key off category_id, not listing_type: on a synced listing
// `listing_type` is Facebook's INVENTORY type — the schema enum is
// ['SINGLE_QUANTITY','EVERGREEN','MULTI_VARIANT'] — and never contains the words
// this function used to look for. Every synced listing therefore fell through to
// 'item', so a property opened its edit form with item fields (condition, brand,
// size) and none of its own (rooms, baths, sqm).
export function resolveListingCategory(listing: any): 'item' | 'vehicle' | 'rental' | 'home_sale' {
  if (!listing) return 'item'
  const byId = FB_CATEGORY_IDS[String(listing.category_id || '')]
  if (byId) return byId
  // Some rows carry our own listing_type ('home_sale' / 'home_rent' / 'vehicle')
  // rather than Facebook's — those still resolve through the text matcher below.
  const text = `${listing.category_name || ''} ${listing.listing_type || ''}`
  if (/SINGLE_QUANTITY|EVERGREEN|MULTI_VARIANT/i.test(String(listing.listing_type || '')) && !listing.category_name) {
    return 'item'
  }
  return getListingCategory(text)
}

function getListingCategory(listingType?: string): 'item' | 'vehicle' | 'rental' | 'home_sale' {
  if (!listingType) return 'item'
  const type = listingType.toUpperCase()
  // Vehicles: VEHICLE, CAR, AUTO, etc.
  if (type.includes('VEHICLE') || type.includes('CAR') || type.includes('AUTO') || type.includes('MOTORCYCLE')) {
    return 'vehicle'
  }
  // Rentals: FOR_RENT, RENTAL, etc.
  if (type.includes('RENT')) {
    return 'rental'
  }
  // Home sales: HOME_SALE, PROPERTY_FOR_SALE, REAL_ESTATE
  if (type.includes('HOME_SALE') || type.includes('PROPERTY') || type.includes('REAL_ESTATE')) {
    return 'home_sale'
  }
  // Default to item (SINGLE_ITEM, FOR_SALE_ITEM, etc.)
  return 'item'
}

// Helper to get Hebrew label for listing type
function getListingTypeLabel(category: string): string {
  switch (category) {
    case 'vehicle': return 'רכב'
    case 'rental': return 'השכרה'
    case 'home_sale': return 'נדל"ן למכירה'
    case 'item': return 'פריט'
    default: return 'פריט'
  }
}

// Edit Synced Listing Modal Component
function EditSyncedListingModal({
  show,
  listing,
  onClose,
  onSuccess
}: {
  show: boolean
  listing: FacebookSyncedListing | null
  onClose: () => void
  onSuccess: () => void
}) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiInstruction, setAiInstruction] = useState('')
  const [price, setPrice] = useState('')
  const [currency, setCurrency] = useState('ILS')
  const [latitude, setLatitude] = useState<string>('')
  const [longitude, setLongitude] = useState<string>('')
  const [condition, setCondition] = useState('')
  const [brand, setBrand] = useState('')
  const [color, setColor] = useState('')
  const [size, setSize] = useState('')
  // Vehicle fields
  const [vehicleMake, setVehicleMake] = useState('')
  const [vehicleModel, setVehicleModel] = useState('')
  const [vehicleYear, setVehicleYear] = useState('')
  const [vehicleTrim, setVehicleTrim] = useState('')
  const [vehicleMileage, setVehicleMileage] = useState('')
  const [vehicleCategory, setVehicleCategory] = useState('')
  const [vehicleFuelType, setVehicleFuelType] = useState('')
  const [vehicleType, setVehicleType] = useState('')
  const [vehicleConditionVal, setVehicleConditionVal] = useState('')
  const [vehicleTransmission, setVehicleTransmission] = useState('automatic')
  const [vehicleExteriorColor, setVehicleExteriorColor] = useState('')
  const [vehicleInteriorColor, setVehicleInteriorColor] = useState('')
  // Property fields (rental / home_sale)
  const [propertyType, setPropertyType] = useState('')
  const [bedrooms, setBedrooms] = useState('')
  const [bathrooms, setBathrooms] = useState('')
  const [squareMeters, setSquareMeters] = useState('')
  // Photo state
  const [photos, setPhotos] = useState<{url: string, id?: string}[]>([])
  const [uploadingPhotos, setUploadingPhotos] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const listingCategory = resolveListingCategory(listing)

  useEffect(() => {
    if (listing) {
      setTitle(listing.title || '')
      setDescription(listing.description || '')
      setPrice(listing.price?.toString() || '')
      setCurrency(listing.currency || 'ILS')
      setLatitude(listing.latitude?.toString() || '')
      setLongitude(listing.longitude?.toString() || '')
      setCondition(listing.condition || '')
      setBrand(listing.raw_data?.brand || '')
      setColor(listing.raw_data?.color || '')
      setSize(listing.raw_data?.size || '')
      setPropertyType((listing as any).home_data?.property_type || listing.raw_data?.property_type || '')
      const _hd = (listing as any).home_data || {}
      const _bd = _hd.bedrooms ?? _hd.rooms ?? listing.raw_data?.bedrooms
      const _ba = _hd.bathrooms ?? listing.raw_data?.bathrooms
      const _sqm = _hd.square_meters ?? _hd.sqm ?? _hd.squareMeters ?? listing.raw_data?.square_meters
      setBedrooms(_bd != null && Number(_bd) > 0 ? String(_bd) : '')
      setBathrooms(_ba != null && Number(_ba) > 0 ? String(_ba) : '')
      setSquareMeters(_sqm != null && Number(_sqm) > 0 ? String(_sqm) : '')
      // Try to extract vehicle data from raw_data or vehicle_data (MyListing format)
      const vd = listing.vehicle_data
      if (listing.raw_data || vd) {
        setVehicleMake(listing.raw_data?.vehicle_make || vd?.make || '')
        setVehicleModel(listing.raw_data?.vehicle_model || vd?.model || '')
        setVehicleYear(listing.raw_data?.vehicle_year?.toString() || vd?.year?.toString() || '')
        setVehicleTrim(listing.raw_data?.vehicle_trim || vd?.trim || '')
        setVehicleMileage(listing.raw_data?.vehicle_mileage || vd?.mileage?.toString() || '')
        setVehicleCategory(listing.raw_data?.vehicle_category || vd?.vehicleCategory || '')
        setVehicleFuelType(listing.raw_data?.vehicle_fuel_type || vd?.fuelType || '')
        setVehicleType(listing.raw_data?.vehicle_type || vd?.vehicleType || '')
        setVehicleConditionVal(listing.raw_data?.vehicle_condition || vd?.vehicleCondition || '')
        setVehicleTransmission((listing.raw_data?.vehicle_transmission || (vd?.isManualTransmission ? 'manual' : undefined)) === 'manual' ? 'manual' : 'automatic')
        setVehicleExteriorColor(listing.raw_data?.vehicle_exterior_color || vd?.exteriorColor || '')
        setVehicleInteriorColor(listing.raw_data?.vehicle_interior_color || vd?.interiorColor || '')
      }
      // Build photos array from listing
      const photoList: {url: string, id?: string}[] = []
      // Try to get photo_ids from raw_data
      const rawPhotoIds = listing.raw_data?.photo_ids || listing.raw_data?.listing_photos?.map((p: any) => p.id) || []
      if (listing.photo_urls && listing.photo_urls.length > 0) {
        listing.photo_urls.forEach((url, idx) => {
          photoList.push({ url, id: rawPhotoIds[idx] })
        })
      } else if (listing.primary_photo_url) {
        photoList.push({ url: listing.primary_photo_url, id: rawPhotoIds[0] })
      }
      setPhotos(photoList)
      setError('')
      setAiInstruction('')
    }
  }, [listing])

  const handlePhotoUpload = async (files: FileList) => {
    if (files.length === 0) return
    setUploadingPhotos(true)
    try {
      const { uploadPhotos } = await import('./core/_requests')
      const result = await uploadPhotos(Array.from(files))
      // Add new photos to the list (without Facebook photo_ids yet - they'll be uploaded during save)
      const newPhotos = result.urls.map(url => ({ url, id: undefined }))
      setPhotos([...photos, ...newPhotos])
    } catch (err) {
      console.error('Failed to upload photos:', err)
      setError('שגיאה בהעלאת התמונות')
    } finally {
      setUploadingPhotos(false)
    }
  }

  const removePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index))
  }

  const movePhoto = (index: number, direction: 'up' | 'down') => {
    const newPhotos = [...photos]
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= photos.length) return
    [newPhotos[index], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[index]]
    setPhotos(newPhotos)
  }

  const handleSubmit = async () => {
    if (!listing) return

    setLoading(true)
    setError('')

    try {
      const updateData: any = {}

      // Always include current values for fields that have data
      if (title) updateData.title = title
      if (description) updateData.description = description
      // Price: accept "2,890,000", "2 890 000 ₪" etc. (the old type=number input
      // turned those into '' and the new price was silently dropped).
      const priceClean = String(price || '').replace(/[^\d.]/g, '')
      const priceNum = priceClean ? parseFloat(priceClean) : NaN
      if (price && !(priceNum > 1)) {
        setError('נא להזין מחיר תקין (מספר גדול מ-1)')
        setLoading(false)
        return
      }
      if (priceNum > 1) {
        updateData.price = priceNum
        updateData.currency = currency
      }

      // Location fields
      const newLat = latitude ? parseFloat(latitude) : null
      const newLng = longitude ? parseFloat(longitude) : null
      if (newLat !== null) {
        updateData.latitude = newLat
      }
      if (newLng !== null) {
        updateData.longitude = newLng
      }

      // Item-specific fields
      if (listingCategory === 'item') {
        if (condition) updateData.condition = condition
        if (brand) updateData.brand = brand
        if (color) updateData.color = color
        if (size) updateData.size = size
      }

      // Vehicle data
      if (listingCategory === 'vehicle') {
        const vehicleData: any = {}
        if (vehicleMake) vehicleData.make = vehicleMake
        if (vehicleModel) vehicleData.model = vehicleModel
        if (vehicleYear) vehicleData.year = parseInt(vehicleYear)
        if (vehicleTrim) vehicleData.trim = vehicleTrim
        if (vehicleMileage) vehicleData.mileage = vehicleMileage
        if (vehicleCategory) vehicleData.vehicleCategory = vehicleCategory
        if (vehicleFuelType) vehicleData.fuelType = vehicleFuelType
        if (vehicleType) vehicleData.vehicleType = vehicleType
        if (vehicleConditionVal) vehicleData.vehicleCondition = vehicleConditionVal
        vehicleData.isManualTransmission = vehicleTransmission === 'manual'
        if (vehicleExteriorColor) vehicleData.exteriorColor = vehicleExteriorColor
        if (vehicleInteriorColor) vehicleData.interiorColor = vehicleInteriorColor
        if (Object.keys(vehicleData).length > 0) {
          updateData.vehicle_data = vehicleData
        }
      }

      // Property data (rental / home_sale)
      if (listingCategory === 'rental' || listingCategory === 'home_sale') {
        const hd: any = {}
        if (propertyType) hd.property_type = propertyType
        if (bedrooms && Number(bedrooms) > 0) hd.bedrooms = parseFloat(bedrooms)
        if (bathrooms && Number(bathrooms) > 0) hd.bathrooms = parseInt(bathrooms)
        if (squareMeters && Number(squareMeters) > 0) hd.square_meters = parseInt(squareMeters)
        if (Object.keys(hd).length > 0) updateData.home_data = hd
      }

      // Build original photos list for comparison
      const originalPhotoUrls = (listing.photo_urls && listing.photo_urls.length) ? listing.photo_urls : (listing.primary_photo_url ? [listing.primary_photo_url] : [])
      const currentPhotoUrls = photos.map(p => p.url)
      const photosChanged = JSON.stringify(originalPhotoUrls) !== JSON.stringify(currentPhotoUrls)

      // Photos — ONLY when the user actually changed them. Synced photos carry
      // no id, so they used to be re-sent as "new" on every edit and the bot
      // wiped the listing's photos and re-uploaded just these (photo loss).
      if (photosChanged) {
        const existingPhotoIds = photos.filter(p => p.id).map(p => p.id)
        const newPhotoUrls = photos.filter(p => !p.id && !originalPhotoUrls.includes(p.url)).map(p => p.url)
        if (existingPhotoIds.length > 0) updateData.photo_ids = existingPhotoIds
        if (newPhotoUrls.length > 0) updateData.new_photo_urls = newPhotoUrls
      }

      // Check if anything changed from original
      const hasChanges =
        title !== listing.title ||
        description !== (listing.description || '') ||
        (priceNum > 1 && priceNum !== listing.price) ||
        (newLat !== null && newLat !== listing.latitude) ||
        (newLng !== null && newLng !== listing.longitude) ||
        (condition && condition !== listing.condition) ||
        (propertyType && propertyType !== ((listing as any).home_data?.property_type || listing.raw_data?.property_type || '')) ||
        (bedrooms !== '' && parseFloat(bedrooms) !== Number((listing as any).home_data?.bedrooms ?? (listing as any).home_data?.rooms)) ||
        (bathrooms !== '' && parseInt(bathrooms) !== Number((listing as any).home_data?.bathrooms)) ||
        (squareMeters !== '' && parseInt(squareMeters) !== Number((listing as any).home_data?.square_meters ?? (listing as any).home_data?.sqm)) ||
        photosChanged

      if (!hasChanges) {
        setError('לא בוצעו שינויים')
        setLoading(false)
        return
      }

      const result = await updateSyncedListing(listing._id, updateData)
      if (result.success) {
        onSuccess()
        onClose()
      } else {
        setError(result.error || 'שגיאה בעדכון המודעה')
      }
    } catch (err: any) {
      console.error('Error updating synced listing:', err)
      setError(err.response?.data?.error || err.message || 'שגיאה בעדכון המודעה')
    } finally {
      setLoading(false)
    }
  }

  if (!show || !listing) return null

  return (
    <div className="marketplace-modal-overlay">
      <div className="marketplace-modal" dir="rtl" style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <div>
            <h2>עריכת מודעה בפייסבוק</h2>
            <p>עדכון המודעה ישפיע ישירות על פייסבוק</p>
          </div>
          <button className="close-btn" onClick={onClose}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-alert">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Listing type badge */}
          <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{
              background: listingCategory === 'vehicle' ? '#0d6efd' :
                         listingCategory === 'rental' ? '#6f42c1' :
                         listingCategory === 'home_sale' ? '#198754' : '#6c757d',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500
            }}>
              {getListingTypeLabel(listingCategory)}
            </span>
            {listing.listing_type && (
              <span style={{ color: '#a1a5b7', fontSize: '11px' }}>
                ({listing.listing_type})
              </span>
            )}
          </div>

          {listing.primary_photo_url && (
            <div style={{ marginBottom: '16px', textAlign: 'center' }}>
              <img
                src={toCdnUrl(listing.primary_photo_url)}
                alt={listing.title}
                style={{ maxHeight: '150px', borderRadius: '8px' }}
              />
            </div>
          )}

          <div className="form-section">
            <div className="form-group">
              <label>כותרת</label>
              {/* Read-only. Facebook does not allow changing a published listing's
                  title, so an editable box here would accept a change we can never
                  push — it would silently revert on the next sync. */}
              <input
                type="text"
                value={title}
                readOnly
                disabled
                title="לא ניתן לערוך כותרת של מודעה שפורסמה — פייסבוק לא מאפשרת זאת"
                style={{background: '#f5f6f8', cursor: 'not-allowed'}}
              />
              <div style={{fontSize: '11px', color: '#a1a5b7', marginTop: '2px'}}>
                לא ניתן לשנות כותרת של מודעה שפורסמה
              </div>
            </div>

            <div className="form-row" style={{ display: 'flex', gap: '12px' }}>
              <div className="form-group" style={{ flex: 2 }}>
                <label>מחיר</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={price}
                  onChange={(e) => setPrice(e.target.value.replace(/[^\d.,\s₪]/g, ''))}
                  placeholder="מחיר"
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>מטבע</label>
                <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  <option value="ILS">₪ שקל</option>
                  <option value="USD">$ דולר</option>
                  <option value="EUR">€ יורו</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <label>תיאור</label>
                <button
                  type="button"
                  disabled={aiLoading}
                  onClick={async () => {
                    setAiLoading(true)
                    setError('')
                    try {
                      const r = await aiSyncedListingDescription(listing._id, {description, instruction: aiInstruction})
                      if (r.description) setDescription(r.description)
                      else setError(r.error || 'יצירת התיאור נכשלה')
                      // Details the AI found in the original post fill only empty fields.
                      const d = r.details || {}
                      if (d.property_type && !propertyType) setPropertyType(d.property_type)
                      if (d.bedrooms && !bedrooms) setBedrooms(String(d.bedrooms))
                      if (d.bathrooms && !bathrooms) setBathrooms(String(d.bathrooms))
                      if (d.square_meters && !squareMeters) setSquareMeters(String(d.square_meters))
                    } catch (err: any) {
                      setError(err.response?.data?.error || 'יצירת התיאור נכשלה')
                    } finally {
                      setAiLoading(false)
                    }
                  }}
                  style={{padding: '4px 12px', background: '#6f42c1', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, cursor: aiLoading ? 'wait' : 'pointer', opacity: aiLoading ? 0.7 : 1, marginBottom: 6}}
                >
                  {aiLoading
                    ? <><span className="spinner-border spinner-border-sm me-1" />כותב...</>
                    : <><i className="bi bi-stars me-1" style={{color: 'white'}} />{description.trim() ? 'שפר עם AI' : 'כתוב עם AI'}</>}
                </button>
              </div>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="תיאור המודעה"
                rows={6}
                maxLength={2000}
              />
              <input
                type="text"
                value={aiInstruction}
                onChange={(e) => setAiInstruction(e.target.value)}
                placeholder="הנחיה ל-AI (לא חובה), למשל: קצר יותר, להדגיש את הנוף"
                style={{marginTop: 6, fontSize: 13}}
              />
            </div>

            {/* Item-specific fields */}
            {listingCategory === 'item' && (
              <>
                <h6 style={{ marginTop: '20px', marginBottom: '12px', color: '#5e6278' }}>
                  <i className="bi bi-box me-2"></i>
                  פרטי הפריט
                </h6>
                <div className="form-group">
                  <label>מצב הפריט *</label>
                  <select
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                    required
                  >
                    <option value="">בחר מצב</option>
                    <option value="new">חדש</option>
                    <option value="used_like_new">משומש - כמו חדש</option>
                    <option value="used_good">משומש - מצב טוב</option>
                    <option value="used_fair">משומש - מצב סביר</option>
                  </select>
                </div>
                <div className="form-row" style={{ display: 'flex', gap: '12px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>מותג</label>
                    <input type="text" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="לדוגמה: Apple, Nike" />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>צבע</label>
                    <input type="text" value={color} onChange={(e) => setColor(e.target.value)} placeholder="לדוגמה: שחור, לבן" />
                  </div>
                </div>
                <div className="form-group">
                  <label>מידה</label>
                  <input type="text" value={size} onChange={(e) => setSize(e.target.value)} placeholder="לדוגמה: M, 42" />
                </div>
              </>
            )}

            {/* Property-specific fields (rental / home_sale) */}
            {(listingCategory === 'rental' || listingCategory === 'home_sale') && (
              <div className="form-group">
                <label>סוג נכס</label>
                <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)}>
                  <option value="">בחר סוג נכס</option>
                  <option value="apartment">דירה</option>
                  <option value="house">בית פרטי</option>
                  <option value="garden_apartment">דירת גן</option>
                  <option value="penthouse">פנטהאוז</option>
                  <option value="duplex">דופלקס</option>
                  <option value="studio">סטודיו</option>
                  <option value="room">חדר</option>
                </select>
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>חדרים</label>
                    <input type="number" min="0" step="0.5" value={bedrooms}
                      onChange={(e) => setBedrooms(e.target.value)} placeholder="מס' חדרים" />
                  </div>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>חדרי רחצה</label>
                    <input type="number" min="0" step="1" value={bathrooms}
                      onChange={(e) => setBathrooms(e.target.value)} placeholder="מס' חדרי רחצה" />
                  </div>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>מ"ר</label>
                    <input type="number" min="0" step="1" value={squareMeters}
                      onChange={(e) => setSquareMeters(e.target.value)} placeholder={'שטח במ"ר'} />
                  </div>
                </div>
              </div>
            )}

            {/* Vehicle-specific fields */}
            {listingCategory === 'vehicle' && (
              <>
                <h6 style={{ marginTop: '20px', marginBottom: '12px', color: '#5e6278' }}>
                  <i className="bi bi-car-front me-2"></i>
                  פרטי הרכב
                </h6>
                <div className="form-group">
                  <label>קטגוריית רכב</label>
                  <select value={vehicleCategory} onChange={(e) => setVehicleCategory(e.target.value)}>
                    <option value="">בחר קטגוריה</option>
                    <option value="car&truck/">רכב / משאית</option>
                    <option value="motorcycle/">אופנוע</option>
                    <option value="powersport/">כלי ספורט ממונע</option>
                    <option value="rv&camper/">קרוואן / קמפר</option>
                    <option value="boat/">סירה</option>
                    <option value="commercial&industrial/">מסחרי / תעשייתי</option>
                    <option value="trailer/">נגרר</option>
                  </select>
                </div>
                <div className="form-row" style={{ display: 'flex', gap: '12px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>יצרן *</label>
                    <input
                      type="text"
                      value={vehicleMake}
                      onChange={(e) => setVehicleMake(e.target.value)}
                      placeholder="לדוגמה: טסלה, טויוטה"
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>דגם *</label>
                    <input
                      type="text"
                      value={vehicleModel}
                      onChange={(e) => setVehicleModel(e.target.value)}
                      placeholder="לדוגמה: Model 3, Camry"
                    />
                  </div>
                </div>
                <div className="form-row" style={{ display: 'flex', gap: '12px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>שנה *</label>
                    <input
                      type="number"
                      value={vehicleYear}
                      onChange={(e) => setVehicleYear(e.target.value)}
                      placeholder="לדוגמה: 2024"
                      min="1900"
                      max="2030"
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>גרסה / Trim</label>
                    <input
                      type="text"
                      value={vehicleTrim}
                      onChange={(e) => setVehicleTrim(e.target.value)}
                      placeholder="לדוגמה: Sport, Limited"
                    />
                  </div>
                </div>
                <div className="form-row" style={{ display: 'flex', gap: '12px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>קילומטראז׳</label>
                    <input
                      type="text"
                      value={vehicleMileage}
                      onChange={(e) => setVehicleMileage(e.target.value)}
                      placeholder="לדוגמה: 50000"
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>סוג דלק</label>
                    <select value={vehicleFuelType} onChange={(e) => setVehicleFuelType(e.target.value)}>
                      <option value="">בחר סוג דלק</option>
                      <option value="GASOLINE">בנזין</option>
                      <option value="DIESEL">דיזל</option>
                      <option value="ELECTRIC">חשמלי</option>
                      <option value="HYBRID">היברידי</option>
                      <option value="FLEX">פלקס</option>
                      <option value="OTHER">אחר</option>
                    </select>
                  </div>
                </div>
                <div className="form-row" style={{ display: 'flex', gap: '12px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>סוג רכב</label>
                    <select value={vehicleType} onChange={(e) => setVehicleType(e.target.value)}>
                      <option value="">בחר סוג</option>
                      <option value="sedan">סדאן</option>
                      <option value="suv">SUV</option>
                      <option value="truck">טנדר / משאית</option>
                      <option value="coupe">קופה</option>
                      <option value="convertible">קבריולט</option>
                      <option value="hatchback">האצ׳בק</option>
                      <option value="wagon">סטיישן</option>
                      <option value="van">ואן</option>
                      <option value="minivan">מיניוואן</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>מצב הרכב</label>
                    <select value={vehicleConditionVal} onChange={(e) => setVehicleConditionVal(e.target.value)}>
                      <option value="">בחר מצב</option>
                      <option value="excellent">מצוין</option>
                      <option value="very_good">טוב מאוד</option>
                      <option value="good">טוב</option>
                      <option value="fair">סביר</option>
                      <option value="poor">גרוע</option>
                    </select>
                  </div>
                </div>
                <div className="form-row" style={{ display: 'flex', gap: '12px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>תיבת הילוכים</label>
                    <select value={vehicleTransmission} onChange={(e) => setVehicleTransmission(e.target.value)}>
                      <option value="automatic">אוטומטית</option>
                      <option value="manual">ידנית</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}></div>
                </div>
                <div className="form-row" style={{ display: 'flex', gap: '12px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>צבע חיצוני</label>
                    <input
                      type="text"
                      value={vehicleExteriorColor}
                      onChange={(e) => setVehicleExteriorColor(e.target.value)}
                      placeholder="לדוגמה: שחור, לבן"
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>צבע פנימי</label>
                    <input
                      type="text"
                      value={vehicleInteriorColor}
                      onChange={(e) => setVehicleInteriorColor(e.target.value)}
                      placeholder="לדוגמה: שחור, בז׳"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Rental-specific fields */}
            {listingCategory === 'rental' && (
              <>
                <h6 style={{ marginTop: '20px', marginBottom: '12px', color: '#5e6278' }}>
                  <i className="bi bi-house me-2"></i>
                  פרטי הנכס להשכרה
                </h6>
                <small style={{ color: '#a1a5b7', display: 'block', marginBottom: '12px' }}>
                  שדות נוספים זמינים ב-Facebook בעת עריכה ישירה
                </small>
              </>
            )}

            {/* Home sale-specific fields */}
            {listingCategory === 'home_sale' && (
              <>
                <h6 style={{ marginTop: '20px', marginBottom: '12px', color: '#5e6278' }}>
                  <i className="bi bi-building me-2"></i>
                  פרטי הנכס למכירה
                </h6>
                <small style={{ color: '#a1a5b7', display: 'block', marginBottom: '12px' }}>
                  שדות נוספים זמינים ב-Facebook בעת עריכה ישירה
                </small>
              </>
            )}

            <h6 style={{ marginTop: '20px', marginBottom: '12px', color: '#5e6278' }}>
              <i className="bi bi-geo-alt me-2"></i>
              מיקום
            </h6>
            <IsraelCitySelector
              value={latitude && longitude ? { lat: parseFloat(latitude), lng: parseFloat(longitude), city: listing?.location } : undefined}
              onChange={(loc) => {
                setLatitude(loc.lat.toString())
                setLongitude(loc.lng.toString())
              }}
              placeholder="חפש עיר..."
            />

            {/* Photos Section */}
            <h6 style={{ marginTop: '20px', marginBottom: '12px', color: '#5e6278' }}>
              <i className="bi bi-images me-2"></i>
              תמונות
            </h6>
            <div style={{ marginBottom: '12px' }}>
              <input
                type="file"
                id="synced-photo-upload"
                multiple
                accept="image/*"
                onChange={(e) => e.target.files && handlePhotoUpload(e.target.files)}
                style={{ display: 'none' }}
              />
              <label
                htmlFor="synced-photo-upload"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  border: '2px dashed #e4e6ef',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: '#5e6278',
                  transition: 'all 0.2s'
                }}
              >
                {uploadingPhotos ? (
                  <><span className="spinner" style={{ width: '16px', height: '16px' }}></span> מעלה...</>
                ) : (
                  <><i className="bi bi-cloud-arrow-up"></i> העלה תמונות</>
                )}
              </label>
            </div>
            {photos.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                gap: '8px'
              }}>
                {photos.map((photo, index) => (
                  <div
                    key={index}
                    style={{
                      position: 'relative',
                      aspectRatio: '1',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid #e4e6ef'
                    }}
                  >
                    <img
                      src={toCdnUrl(photo.url)}
                      alt={`תמונה ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    {index === 0 && (
                      <span style={{
                        position: 'absolute',
                        bottom: '4px',
                        left: '4px',
                        background: '#009ef7',
                        color: 'white',
                        fontSize: '10px',
                        padding: '2px 6px',
                        borderRadius: '4px'
                      }}>ראשית</span>
                    )}
                    <div style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      display: 'flex',
                      gap: '2px'
                    }}>
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => movePhoto(index, 'up')}
                          style={{
                            width: '24px',
                            height: '24px',
                            border: 'none',
                            borderRadius: '4px',
                            background: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px'
                          }}
                        >
                          <i className="bi bi-arrow-right"></i>
                        </button>
                      )}
                      {index < photos.length - 1 && (
                        <button
                          type="button"
                          onClick={() => movePhoto(index, 'down')}
                          style={{
                            width: '24px',
                            height: '24px',
                            border: 'none',
                            borderRadius: '4px',
                            background: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px'
                          }}
                        >
                          <i className="bi bi-arrow-left"></i>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        style={{
                          width: '24px',
                          height: '24px',
                          border: 'none',
                          borderRadius: '4px',
                          background: 'rgba(220,53,69,0.9)',
                          color: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px'
                        }}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {photos.length === 0 && (
              <div style={{ color: '#a1a5b7', textAlign: 'center', padding: '20px' }}>
                אין תמונות
              </div>
            )}
          </div>

          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            padding: '12px',
            marginTop: '16px',
            fontSize: '13px'
          }}>
            <i className="bi bi-info-circle me-2"></i>
            שים לב: העדכון ישלח לפייסבוק דרך האוטומציה. ודא שהפרופיל מחובר ופעיל.
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={loading}>
            ביטול
          </button>
          <button
            type="button"
            className="btn-submit"
            onClick={handleSubmit}
            disabled={loading}
            style={{ background: '#1877f2' }}
          >
            {loading ? (
              <><span className="spinner"></span> מעדכן...</>
            ) : (
              <><i className="bi bi-facebook me-2"></i>עדכן בפייסבוק</>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// Listing Details Modal Component - shows comments, conversations, cross-post groups, auto-reply
function ListingDetailsModal({
  show,
  listing,
  onClose,
  onUpdate,
  initialTab = 'info'
}: {
  show: boolean
  listing: FacebookSyncedListing | null
  onClose: () => void
  onUpdate: () => void
  initialTab?: 'info' | 'comments' | 'messages' | 'groups'
}) {
  const [activeTab, setActiveTab] = useState<'info' | 'comments' | 'messages' | 'groups'>(initialTab)

  // Reset to initialTab when modal opens with new initialTab
  useEffect(() => {
    if (show) {
      setActiveTab(initialTab)
    }
  }, [show, initialTab])
  const [autoReplyMessage, setAutoReplyMessage] = useState('')
  const [savingAutoReply, setSavingAutoReply] = useState(false)
  const [autoReplySuccess, setAutoReplySuccess] = useState(false)

  // Reply state
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  const [replyMessage, setReplyMessage] = useState('')
  const [sendingReply, setSendingReply] = useState(false)
  const [replySuccess, setReplySuccess] = useState<string | null>(null)

  // Mark available/sold state
  const [markingAvailable, setMarkingAvailable] = useState(false)
  const [markAvailableSuccess, setMarkAvailableSuccess] = useState(false)
  const [markingSold, setMarkingSold] = useState(false)
  const [markSoldSuccess, setMarkSoldSuccess] = useState(false)

  // Local state for listing status (to update UI immediately)
  const [localIsLive, setLocalIsLive] = useState(listing?.is_live ?? false)
  const [localIsSold, setLocalIsSold] = useState(listing?.is_sold ?? false)

  // Crosspost state
  const [crossposting, setCrossposting] = useState(false)
  const [crosspostSuccess, setCrosspostSuccess] = useState(false)
  const [crosspostCount, setCrosspostCount] = useState(0)

  // Reply all missing state
  const [replyingAllMissing, setReplyingAllMissing] = useState(false)
  const [replyAllMissingSuccess, setReplyAllMissingSuccess] = useState(false)

  // Sync messages state
  const [syncingMessages, setSyncingMessages] = useState(false)
  const [syncMessagesSuccess, setSyncMessagesSuccess] = useState(false)

  // Auto-refresh messages every 30 minutes when Messages tab is open
  useEffect(() => {
    if (!show || activeTab !== 'messages' || !listing) return

    const intervalId = setInterval(() => {
      console.log('Auto-refreshing messages...')
      onUpdate()
    }, 30 * 60 * 1000) // 30 minutes

    return () => clearInterval(intervalId)
  }, [show, activeTab, listing, onUpdate])

  // Format time since last sync
  const formatTimeSinceSync = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'לא סונכרן'
    const lastSync = new Date(dateStr).getTime()
    const now = Date.now()
    const diffMinutes = Math.floor((now - lastSync) / (60 * 1000))

    if (diffMinutes < 1) return 'עכשיו'
    if (diffMinutes < 60) return `לפני ${diffMinutes} דקות`
    const diffHours = Math.floor(diffMinutes / 60)
    if (diffHours < 24) return `לפני ${diffHours} שעות`
    const diffDays = Math.floor(diffHours / 24)
    return `לפני ${diffDays} ימים`
  }

  // Excluded groups state (groups that won't be posted to)
  const [excludedGroups, setExcludedGroups] = useState<Set<string>>(new Set())
  const [savingExcludedGroups, setSavingExcludedGroups] = useState(false)
  const [excludedGroupsSaved, setExcludedGroupsSaved] = useState(false)

  // Toggle group exclusion
  const toggleGroupExclusion = (groupId: string) => {
    setExcludedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
    setExcludedGroupsSaved(false) // Mark as unsaved when changed
  }

  // Select all unpublished groups (remove from excluded)
  const selectAllGroups = () => {
    setExcludedGroups(new Set())
    setExcludedGroupsSaved(false)
  }

  // Deselect all unpublished groups (add all to excluded)
  const deselectAllGroups = () => {
    if (!listing?.available_crosspost_groups) return
    const allUnpostedGroupIds = listing.available_crosspost_groups
      .filter(g => !g.is_posted)
      .map(g => g.group_id)
    setExcludedGroups(new Set(allUnpostedGroupIds))
    setExcludedGroupsSaved(false)
  }

  // Save excluded groups for next time
  const saveExcludedGroups = async () => {
    if (!listing) return
    setSavingExcludedGroups(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/synced-listings/${listing._id}/excluded-groups`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}').api_token}`
        },
        body: JSON.stringify({
          excluded_group_ids: Array.from(excludedGroups)
        })
      })
      if (response.ok) {
        setExcludedGroupsSaved(true)
        setTimeout(() => setExcludedGroupsSaved(false), 3000)
      }
    } catch (err) {
      console.error('Failed to save excluded groups:', err)
    } finally {
      setSavingExcludedGroups(false)
    }
  }

  useEffect(() => {
    if (listing) {
      setAutoReplyMessage(listing.auto_reply_message || '')
      // Load saved excluded groups from listing, or reset to empty
      setExcludedGroups(new Set((listing as any).excluded_group_ids || []))
      setExcludedGroupsSaved(true) // Mark as saved since we just loaded
      setAutoReplySuccess(false)
      setReplyingTo(null)
      setReplyMessage('')
      setMarkAvailableSuccess(false)
      setMarkSoldSuccess(false)
      setCrosspostSuccess(false)
      setLocalIsLive(listing.is_live ?? false)
      setLocalIsSold(listing.is_sold ?? false)
    }
  }, [listing])

  const handleMarkAvailable = async () => {
    if (!listing) return
    setMarkingAvailable(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/synced-listings/${listing._id}/mark-available`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}').api_token}`
        }
      })
      const result = await response.json()
      if (result.success) {
        setMarkAvailableSuccess(true)
        // Update local state immediately for UI
        setLocalIsLive(true)
        setLocalIsSold(false)
        onUpdate()
        setTimeout(() => setMarkAvailableSuccess(false), 5000)
      }
    } catch (err) {
      console.error('Failed to mark available:', err)
    } finally {
      setMarkingAvailable(false)
    }
  }

  const handleMarkSold = async () => {
    if (!listing) return
    setMarkingSold(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/synced-listings/${listing._id}/mark-sold`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}').api_token}`
        }
      })
      const result = await response.json()
      if (result.success) {
        setMarkSoldSuccess(true)
        // Update local state immediately for UI
        setLocalIsSold(true)
        setLocalIsLive(false)
        onUpdate()
        setTimeout(() => setMarkSoldSuccess(false), 5000)
      }
    } catch (err) {
      console.error('Failed to mark sold:', err)
    } finally {
      setMarkingSold(false)
    }
  }

  const handleCrosspost = async () => {
    if (!listing) return

    // Get available groups that haven't been posted to and aren't excluded
    const availableGroups = listing.available_crosspost_groups?.filter(g => !g.is_posted && !excludedGroups.has(g.group_id)) || []
    if (availableGroups.length === 0) {
      alert('אין קבוצות זמינות לפרסום')
      return
    }

    setCrossposting(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/synced-listings/${listing._id}/crosspost`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}').api_token}`
        },
        body: JSON.stringify({
          chunk_size: 20,
          group_ids: availableGroups.map(g => g.group_id) // Only send included groups
        })
      })
      const result = await response.json()
      if (result.success) {
        setCrosspostSuccess(true)
        setCrosspostCount(result.groups_count || availableGroups.length)
        onUpdate()
        setTimeout(() => setCrosspostSuccess(false), 5000)
      }
    } catch (err) {
      console.error('Failed to crosspost:', err)
    } finally {
      setCrossposting(false)
    }
  }

  // Get count of groups that will be posted to (not posted and not excluded)
  const getIncludedGroupsCount = () => {
    if (!listing?.available_crosspost_groups) return 0
    return listing.available_crosspost_groups.filter(g => !g.is_posted && !excludedGroups.has(g.group_id)).length
  }

  const handleSaveAutoReply = async () => {
    if (!listing) return
    setSavingAutoReply(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/synced-listings/${listing._id}/auto-reply`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}').api_token}`
        },
        body: JSON.stringify({ auto_reply_message: autoReplyMessage })
      })
      const result = await response.json()
      if (result.success) {
        setAutoReplySuccess(true)
        onUpdate()
        setTimeout(() => setAutoReplySuccess(false), 3000)
      }
    } catch (err) {
      console.error('Failed to save auto-reply:', err)
    } finally {
      setSavingAutoReply(false)
    }
  }

  const handleReplyAllMissing = async () => {
    if (!listing || !autoReplyMessage.trim()) return
    setReplyingAllMissing(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/synced-listings/${listing._id}/reply-all-missing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}').api_token}`
        }
      })
      const result = await response.json()
      if (result.success) {
        setReplyAllMissingSuccess(true)
        setTimeout(() => setReplyAllMissingSuccess(false), 3000)
      }
    } catch (err) {
      console.error('Failed to reply all missing:', err)
    } finally {
      setReplyingAllMissing(false)
    }
  }

  const handleSyncMessages = async () => {
    if (!listing) return
    setSyncingMessages(true)
    try {
      const result = await syncListingMessages(listing._id)
      if (result.success) {
        setSyncMessagesSuccess(true)
        onUpdate() // Refresh the listing data
        setTimeout(() => setSyncMessagesSuccess(false), 3000)
      }
    } catch (err) {
      console.error('Failed to sync messages:', err)
    } finally {
      setSyncingMessages(false)
    }
  }

  const handleSendReply = async (threadFbid: string, participantId?: string, messageOverride?: string) => {
    const messageToSend = messageOverride || replyMessage
    if (!listing || !messageToSend.trim()) return
    setSendingReply(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/synced-listings/${listing._id}/send-reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}').api_token}`
        },
        body: JSON.stringify({
          thread_fbid: threadFbid,
          recipient_facebook_id: participantId,
          message: messageToSend
        })
      })
      const result = await response.json()
      if (result.success) {
        setReplySuccess(threadFbid)
        setReplyMessage('')
        setReplyingTo(null)
        // Reply is queued - refresh after delay to check if it was actually sent
        setTimeout(() => {
          onUpdate()
          setReplySuccess(null)
        }, 15000)
      }
    } catch (err) {
      console.error('Failed to send reply:', err)
    } finally {
      setSendingReply(false)
    }
  }

  if (!show || !listing) return null

  const conditionInfo = getConditionLabel(listing.condition)

  return (
    <div className="marketplace-modal-overlay">
      <div className="marketplace-modal" dir="rtl" style={{ maxWidth: '700px', maxHeight: '90vh' }}>
        <div className="modal-header">
          <div>
            <h2>פרטי מודעה</h2>
            <p style={{ fontSize: '13px', color: '#a1a5b7' }}>{listing.title}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Mark Sold Button - show if live and not sold */}
            {(localIsLive && !localIsSold) && (
              markSoldSuccess ? (
                <span style={{
                  padding: '8px 16px',
                  background: '#d1e7dd',
                  color: '#0f5132',
                  borderRadius: '6px',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <i className="bi bi-check-circle"></i>
                  הפקודה נשלחה!
                </span>
              ) : (
                <button
                  onClick={handleMarkSold}
                  disabled={markingSold}
                  style={{
                    padding: '8px 16px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: markingSold ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {markingSold ? (
                    <><span className="spinner" style={{ width: '14px', height: '14px' }}></span> מסמן...</>
                  ) : (
                    <><i className="bi bi-bag-check"></i> סמן כנמכר</>
                  )}
                </button>
              )
            )}
            {/* Mark Available Button - show if not live or sold */}
            {(localIsSold || !localIsLive) && (
              markAvailableSuccess ? (
                <span style={{
                  padding: '8px 16px',
                  background: '#d1e7dd',
                  color: '#0f5132',
                  borderRadius: '6px',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <i className="bi bi-check-circle"></i>
                  הפקודה נשלחה!
                </span>
              ) : (
                <button
                  onClick={handleMarkAvailable}
                  disabled={markingAvailable}
                  style={{
                    padding: '8px 16px',
                    background: '#198754',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: markingAvailable ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {markingAvailable ? (
                    <><span className="spinner" style={{ width: '14px', height: '14px' }}></span> מסמן...</>
                  ) : (
                    <><i className="bi bi-check2-circle"></i> סמן כזמין</>
                  )}
                </button>
              )
            )}
            {/* Crosspost to Groups Button */}
            {listing.available_crosspost_groups && listing.available_crosspost_groups.filter(g => !g.is_posted).length > 0 && (
              crosspostSuccess ? (
                <span style={{
                  padding: '8px 16px',
                  background: '#d1e7dd',
                  color: '#0f5132',
                  borderRadius: '6px',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <i className="bi bi-check-circle"></i>
                  נשלח ל-{crosspostCount} קבוצות!
                </span>
              ) : (
                <button
                  onClick={handleCrosspost}
                  disabled={crossposting}
                  style={{
                    padding: '8px 16px',
                    background: '#0d6efd',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: crossposting ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {crossposting ? (
                    <><span className="spinner" style={{ width: '14px', height: '14px' }}></span> משתף...</>
                  ) : (
                    <><i className="bi bi-share"></i> שתף ל-{listing.available_crosspost_groups.filter(g => !g.is_posted).length} קבוצות</>
                  )}
                </button>
              )
            )}
            <button className="close-btn" onClick={onClose}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', padding: '0 20px', borderBottom: '1px solid #e4e6ef' }}>
          {[
            { id: 'info', label: 'מידע', icon: 'info-circle' },
            { id: 'comments', label: `תגובות (${listing.comments?.length || 0})`, icon: 'chat-left-text' },
            { id: 'messages', label: `הודעות (${listing.conversations?.length || 0})`, icon: 'chat-dots' },
            { id: 'groups', label: `קבוצות (${listing.available_crosspost_groups?.length || 0})`, icon: 'people' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id as any)
                // Update URL with tab parameter
                if (listing) {
                  const params = new URLSearchParams(window.location.search)
                  params.set('listing', listing._id)
                  params.set('tab', tab.id)
                  window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`)
                }
              }}
              style={{
                padding: '12px 16px',
                border: 'none',
                background: activeTab === tab.id ? '#f1f1f2' : 'transparent',
                color: activeTab === tab.id ? '#181c32' : '#a1a5b7',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: activeTab === tab.id ? 600 : 400,
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <i className={`bi bi-${tab.icon}`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {/* Info Tab */}
          {activeTab === 'info' && (
            <div>
              {/* Stats Summary */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px',
                marginBottom: '20px'
              }}>
                <div style={{ textAlign: 'center', padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 600, color: '#181c32' }}>{listing.views_count || 0}</div>
                  <div style={{ fontSize: '12px', color: '#a1a5b7' }}>צפיות</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 600, color: '#181c32' }}>{listing.messages_count || listing.conversations?.length || 0}</div>
                  <div style={{ fontSize: '12px', color: '#a1a5b7' }}>הודעות</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 600, color: '#181c32' }}>{listing.saves_count || 0}</div>
                  <div style={{ fontSize: '12px', color: '#a1a5b7' }}>שמירות</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 600, color: '#181c32' }}>{listing.shares_count || 0}</div>
                  <div style={{ fontSize: '12px', color: '#a1a5b7' }}>שיתופים</div>
                </div>
              </div>

              {/* Item Details */}
              <h6 style={{ marginBottom: '12px', color: '#5e6278' }}>
                <i className="bi bi-tag me-2"></i>
                פרטי הפריט
              </h6>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px' }}>
                {listing.condition && (
                  <div>
                    <span style={{ fontSize: '12px', color: '#a1a5b7' }}>מצב:</span>
                    <span style={{
                      marginRight: '8px',
                      background: conditionInfo.color,
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>{conditionInfo.label}</span>
                  </div>
                )}
                {listing.brand && (
                  <div>
                    <span style={{ fontSize: '12px', color: '#a1a5b7' }}>מותג:</span>
                    <span style={{ marginRight: '8px', color: '#181c32' }}>{listing.brand}</span>
                  </div>
                )}
                {listing.color && (
                  <div>
                    <span style={{ fontSize: '12px', color: '#a1a5b7' }}>צבע:</span>
                    <span style={{ marginRight: '8px', color: '#181c32' }}>{listing.color}</span>
                  </div>
                )}
                {listing.size && (
                  <div>
                    <span style={{ fontSize: '12px', color: '#a1a5b7' }}>מידה:</span>
                    <span style={{ marginRight: '8px', color: '#181c32' }}>{listing.size}</span>
                  </div>
                )}
                {listing.material && (
                  <div>
                    <span style={{ fontSize: '12px', color: '#a1a5b7' }}>חומר:</span>
                    <span style={{ marginRight: '8px', color: '#181c32' }}>{listing.material}</span>
                  </div>
                )}
                {listing.model && (
                  <div>
                    <span style={{ fontSize: '12px', color: '#a1a5b7' }}>דגם:</span>
                    <span style={{ marginRight: '8px', color: '#181c32' }}>{listing.model}</span>
                  </div>
                )}
              </div>

              {/* Location */}
              {(listing.location || listing.latitude) && (
                <>
                  <h6 style={{ marginBottom: '12px', color: '#5e6278' }}>
                    <i className="bi bi-geo-alt me-2"></i>
                    מיקום
                  </h6>
                  <div style={{ marginBottom: '20px', padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                    {listing.location && <div style={{ color: '#181c32', marginBottom: '4px' }}>{listing.location}</div>}
                    {listing.latitude && listing.longitude && (
                      <div style={{ fontSize: '12px', color: '#a1a5b7' }}>
                        קואורדינטות: {listing.latitude.toFixed(6)}, {listing.longitude.toFixed(6)}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Hashtags */}
              {listing.hashtags && listing.hashtags.length > 0 && (
                <>
                  <h6 style={{ marginBottom: '12px', color: '#5e6278' }}>
                    <i className="bi bi-hash me-2"></i>
                    האשטאגים
                  </h6>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '20px' }}>
                    {listing.hashtags.map((tag, idx) => (
                      <span key={idx} style={{
                        background: '#e7f1ff',
                        color: '#0d6efd',
                        padding: '4px 10px',
                        borderRadius: '16px',
                        fontSize: '12px'
                      }}>
                        #{tag}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Comments Tab */}
          {activeTab === 'comments' && (
            <div>
              {(!listing.comments || listing.comments.length === 0) ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#a1a5b7' }}>
                  <i className="bi bi-chat-left-text" style={{ fontSize: '48px', marginBottom: '12px', display: 'block' }}></i>
                  <div>אין תגובות למודעה זו</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {listing.comments.map((comment, idx) => (
                    <div key={idx} style={{
                      padding: '12px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      borderRight: '3px solid #0d6efd'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontWeight: 600, color: '#181c32' }}>{comment.author_name}</span>
                        <span style={{ fontSize: '12px', color: '#a1a5b7' }}>
                          {comment.created_time ? new Date(comment.created_time).toLocaleString('he-IL') : ''}
                        </span>
                      </div>
                      <div style={{ color: '#5e6278' }}>{comment.text}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div>
              {/* Auto-Reply Message */}
              <div style={{ marginBottom: '20px', padding: '16px', background: '#f1f3f9', borderRadius: '8px' }}>
                <h6 style={{ marginBottom: '12px', color: '#5e6278' }}>
                  <i className="bi bi-reply me-2"></i>
                  הודעה אוטומטית לפניות
                </h6>
                <textarea
                  value={autoReplyMessage}
                  onChange={(e) => setAutoReplyMessage(e.target.value)}
                  placeholder="הקלד הודעה שתשלח אוטומטית לכל פנייה חדשה על מודעה זו..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #e4e6ef',
                    borderRadius: '8px',
                    resize: 'vertical',
                    fontSize: '14px',
                    background: 'white'
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#a1a5b7' }}>
                    ההודעה תישלח אוטומטית כתשובה לכל הודעה חדשה
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleReplyAllMissing}
                      disabled={replyingAllMissing || !autoReplyMessage.trim() || !listing?.conversations?.length}
                      title={!listing?.conversations?.length ? 'אין הודעות' : !autoReplyMessage.trim() ? 'הזן הודעה תחילה' : 'שלח לכל ההודעות שלא נענו'}
                      style={{
                        padding: '8px 16px',
                        background: replyAllMissingSuccess ? '#198754' : '#6f42c1',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (replyingAllMissing || !autoReplyMessage.trim() || !listing?.conversations?.length) ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        opacity: (!autoReplyMessage.trim() || !listing?.conversations?.length) ? 0.6 : 1
                      }}
                    >
                      {replyingAllMissing ? (
                        <><span className="spinner" style={{ width: '14px', height: '14px' }}></span> שולח...</>
                      ) : replyAllMissingSuccess ? (
                        <><i className="bi bi-check-lg"></i> נשלח!</>
                      ) : (
                        <><i className="bi bi-reply-all"></i> ענה לכולם</>
                      )}
                    </button>
                    <button
                      onClick={handleSaveAutoReply}
                      disabled={savingAutoReply}
                      style={{
                        padding: '8px 16px',
                        background: autoReplySuccess ? '#198754' : '#009ef7',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: savingAutoReply ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      {savingAutoReply ? (
                        <><span className="spinner" style={{ width: '14px', height: '14px' }}></span> שומר...</>
                      ) : autoReplySuccess ? (
                        <><i className="bi bi-check-lg"></i> נשמר!</>
                      ) : (
                        <><i className="bi bi-save"></i> שמור</>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Sync Messages Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
                padding: '12px',
                background: '#e8f4f8',
                borderRadius: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="bi bi-clock-history" style={{ color: '#0d6efd' }}></i>
                  <span style={{ fontSize: '13px', color: '#5e6278' }}>
                    סנכרון אחרון: {formatTimeSinceSync(listing.last_messages_synced_at || listing.last_synced_at)}
                  </span>
                </div>
                <button
                  onClick={handleSyncMessages}
                  disabled={syncingMessages}
                  style={{
                    padding: '6px 14px',
                    background: syncMessagesSuccess ? '#198754' : '#0d6efd',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: syncingMessages ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {syncingMessages ? (
                    <><span className="spinner" style={{ width: '12px', height: '12px' }}></span> מסנכרן...</>
                  ) : syncMessagesSuccess ? (
                    <><i className="bi bi-check-lg"></i> סונכרן!</>
                  ) : (
                    <><i className="bi bi-arrow-clockwise"></i> סנכרן עכשיו</>
                  )}
                </button>
              </div>

              {/* Conversations List */}
              {(!listing.conversations || listing.conversations.length === 0) ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#a1a5b7' }}>
                  <i className="bi bi-chat-dots" style={{ fontSize: '48px', marginBottom: '12px', display: 'block' }}></i>
                  <div>אין הודעות למודעה זו</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {listing.conversations.map((conv, idx) => (
                    <div key={idx} style={{
                      padding: '12px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      borderRight: conv.unread_count > 0 ? '3px solid #dc3545' : '3px solid #6c757d'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontWeight: 600, color: '#181c32', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {conv.participant_name}
                          <a
                            href={`https://www.facebook.com/messages/t/${conv.thread_fbid || conv.participant_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="פתח בממסנג'ר"
                            style={{ color: '#0d6efd', fontSize: '14px' }}
                          >
                            <i className="bi bi-messenger"></i>
                          </a>
                          {conv.unread_count > 0 && (
                            <span style={{
                              background: '#dc3545',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px'
                            }}>
                              {conv.unread_count} חדשות
                            </span>
                          )}
                        </span>
                        <span style={{ fontSize: '12px', color: '#a1a5b7' }}>
                          {conv.last_message_time ? new Date(conv.last_message_time).toLocaleString('he-IL') : ''}
                        </span>
                      </div>
                      <div style={{ color: '#5e6278', fontSize: '13px', marginBottom: '8px' }}>{conv.last_message}</div>

                      {/* Reply section */}
                      {(() => {
                        const threadId = conv.thread_fbid || conv.participant_id
                        const wasReplied = listing.auto_replied_threads?.find(r => r.thread_fbid === threadId)
                        const wasFailed = listing.failed_reply_threads?.find(r => r.thread_fbid === threadId)

                        if (replySuccess === threadId) {
                          return (
                            <div style={{
                              padding: '8px 12px',
                              background: '#d1e7dd',
                              color: '#0f5132',
                              borderRadius: '6px',
                              fontSize: '13px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span className="spinner" style={{ width: '14px', height: '14px' }}></span>
                              הפקודה נשלחה, ממתין לאישור שליחה...
                            </div>
                          )
                        }

                        if (wasReplied) {
                          return (
                            <>
                              <div style={{
                                padding: '8px 12px',
                                background: '#e8f5e9',
                                color: '#2e7d32',
                                borderRadius: '6px',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <i className="bi bi-check-circle-fill"></i>
                                  נשלח ב-{new Date(wasReplied.replied_at).toLocaleString('he-IL')}
                                </span>
                                <button
                                  onClick={async () => {
                                    try {
                                      await unmarkThreadReplied(listing._id, threadId)
                                      onUpdate()
                                    } catch (err) {
                                      console.error('Failed to unmark:', err)
                                    }
                                  }}
                                  title="סמן כלא נשלח - כדי לשלוח שוב"
                                  style={{
                                    padding: '4px 10px',
                                    background: '#fff3cd',
                                    color: '#856404',
                                    border: '1px solid #ffc107',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                >
                                  <i className="bi bi-arrow-counterclockwise"></i>
                                  שלח שוב
                                </button>
                              </div>
                              {/* Show screenshot proof only for admin */}
                              {wasReplied.reply_screenshot && localStorage.getItem('admin_auth_token') && (
                                <div style={{ marginTop: '6px' }}>
                                  <a href={wasReplied.reply_screenshot} target="_blank" rel="noopener noreferrer">
                                    <img
                                      src={wasReplied.reply_screenshot}
                                      alt="Screenshot proof"
                                      style={{
                                        maxWidth: '100%',
                                        maxHeight: '200px',
                                        borderRadius: '6px',
                                        border: '1px solid #e4e6ef',
                                        cursor: 'pointer'
                                      }}
                                    />
                                  </a>
                                </div>
                              )}
                            </>
                          )
                        }

                        if (wasFailed && !wasReplied) {
                          return (
                            <>
                              <div style={{
                                padding: '8px 12px',
                                background: '#f8d7da',
                                color: '#842029',
                                borderRadius: '6px',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <i className="bi bi-x-circle-fill"></i>
                                  שליחה נכשלה ב-{new Date(wasFailed.failed_at).toLocaleString('he-IL')}
                                  {wasFailed.error && <span style={{ fontSize: '11px', opacity: 0.8 }}>({wasFailed.error})</span>}
                                </span>
                              </div>
                              <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                                {listing.auto_reply_message && (
                                  <button
                                    onClick={() => handleSendReply(threadId, conv.participant_id, listing.auto_reply_message)}
                                    disabled={sendingReply}
                                    style={{
                                      padding: '6px 12px',
                                      background: '#198754',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: sendingReply ? 'not-allowed' : 'pointer',
                                      fontSize: '12px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                  >
                                    <i className="bi bi-arrow-clockwise"></i>
                                    נסה שוב
                                  </button>
                                )}
                              </div>
                            </>
                          )
                        }

                        if (replyingTo === threadId) {
                          return (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                              <input
                                type="text"
                                value={replyMessage}
                                onChange={(e) => setReplyMessage(e.target.value)}
                                placeholder="הקלד תשובה..."
                                style={{
                                  flex: 1,
                                  padding: '8px 12px',
                                  border: '1px solid #e4e6ef',
                                  borderRadius: '6px',
                                  fontSize: '13px'
                                }}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && replyMessage.trim()) {
                                    handleSendReply(threadId, conv.participant_id)
                                  }
                                }}
                                autoFocus
                              />
                              <button
                                onClick={() => handleSendReply(threadId, conv.participant_id)}
                                disabled={sendingReply || !replyMessage.trim()}
                                style={{
                                  padding: '8px 16px',
                                  background: '#009ef7',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: sendingReply || !replyMessage.trim() ? 'not-allowed' : 'pointer',
                                  fontSize: '13px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                {sendingReply ? (
                                  <><span className="spinner" style={{ width: '14px', height: '14px' }}></span></>
                                ) : (
                                  <><i className="bi bi-send"></i> שלח</>
                                )}
                              </button>
                              <button
                                onClick={() => { setReplyingTo(null); setReplyMessage(''); }}
                                style={{
                                  padding: '8px 12px',
                                  background: '#f1f1f2',
                                  color: '#5e6278',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '13px'
                                }}
                              >
                                <i className="bi bi-x"></i>
                              </button>
                            </div>
                          )
                        }

                        return (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {listing.auto_reply_message && (
                              <button
                                onClick={() => handleSendReply(threadId, conv.participant_id, listing.auto_reply_message)}
                                disabled={sendingReply}
                                style={{
                                  padding: '6px 12px',
                                  background: '#198754',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: sendingReply ? 'not-allowed' : 'pointer',
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                <i className="bi bi-send-fill"></i>
                                שלח הודעה אוטומטית
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setReplyingTo(threadId)
                                setReplyMessage(listing.auto_reply_message || '')
                              }}
                              style={{
                                padding: '6px 12px',
                                background: '#e7f1ff',
                                color: '#0d6efd',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <i className="bi bi-reply"></i>
                              השב
                            </button>
                          </div>
                        )
                      })()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Groups Tab */}
          {activeTab === 'groups' && (
            <div>
              {(!listing.available_crosspost_groups || listing.available_crosspost_groups.length === 0) ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#a1a5b7' }}>
                  <i className="bi bi-people" style={{ fontSize: '48px', marginBottom: '12px', display: 'block' }}></i>
                  <div>אין קבוצות זמינות לשיתוף</div>
                </div>
              ) : (
                <>
                  {/* Groups header with select all/deselect all */}
                  <div style={{
                    marginBottom: '16px',
                    padding: '12px',
                    background: '#f1f3f9',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <h6 style={{ margin: 0, color: '#5e6278' }}>
                        <i className="bi bi-people me-2"></i>
                        קבוצות ({listing.available_crosspost_groups.length})
                      </h6>
                      <span style={{ fontSize: '12px', color: '#a1a5b7' }}>
                        {getIncludedGroupsCount()} נבחרו
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={selectAllGroups}
                        style={{
                          background: '#e8f4f8',
                          color: '#009ef7',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 500
                        }}
                      >
                        <i className="bi bi-check-all me-1"></i>
                        בחר הכל
                      </button>
                      <button
                        onClick={deselectAllGroups}
                        style={{
                          background: '#fff3cd',
                          color: '#856404',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 500
                        }}
                      >
                        <i className="bi bi-x-lg me-1"></i>
                        נקה
                      </button>
                      <button
                        onClick={saveExcludedGroups}
                        disabled={savingExcludedGroups || excludedGroupsSaved}
                        style={{
                          background: excludedGroupsSaved ? '#198754' : '#6f42c1',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          cursor: savingExcludedGroups ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                          fontWeight: 500
                        }}
                        title="שמור את בחירת הקבוצות לפעם הבאה"
                      >
                        {savingExcludedGroups ? (
                          <><span className="spinner-border spinner-border-sm" style={{ width: '10px', height: '10px' }}></span> שומר...</>
                        ) : excludedGroupsSaved ? (
                          <><i className="bi bi-check-lg me-1"></i>נשמר</>
                        ) : (
                          <><i className="bi bi-save me-1"></i>שמור</>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Post to groups button */}
                  {getIncludedGroupsCount() > 0 && (
                    <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <button
                        onClick={handleCrosspost}
                        disabled={crossposting}
                        style={{
                          background: '#1877f2',
                          color: 'white',
                          border: 'none',
                          padding: '10px 20px',
                          borderRadius: '8px',
                          cursor: crossposting ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          fontWeight: 500
                        }}
                      >
                        {crossposting ? (
                          <><span className="spinner" style={{ width: '14px', height: '14px' }}></span> מפרסם...</>
                        ) : crosspostSuccess ? (
                          <><i className="bi bi-check-lg"></i> פורסם ל-{crosspostCount} קבוצות!</>
                        ) : (
                          <><i className="bi bi-share"></i> שתף ל-{getIncludedGroupsCount()} קבוצות נבחרות</>
                        )}
                      </button>
                      <span style={{ fontSize: '12px', color: '#a1a5b7' }}>
                        לחץ על קבוצה כדי להסיר/להוסיף
                      </span>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {listing.available_crosspost_groups.map((group, idx) => {
                      const isExcluded = excludedGroups.has(group.group_id)
                      return (
                        <div
                          key={idx}
                          onClick={() => !group.is_posted && toggleGroupExclusion(group.group_id)}
                          style={{
                            padding: '12px',
                            background: group.is_posted ? '#f8f9fa' : isExcluded ? '#fff3cd' : '#f8f9fa',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: group.is_posted ? 'default' : 'pointer',
                            opacity: isExcluded ? 0.6 : 1,
                            border: isExcluded ? '1px dashed #ffc107' : '1px solid transparent',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <span style={{
                            color: isExcluded ? '#856404' : '#181c32',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            {!group.is_posted && (
                              <i className={`bi ${isExcluded ? 'bi-square' : 'bi-check-square-fill'}`}
                                style={{ color: isExcluded ? '#ffc107' : '#198754' }}
                              ></i>
                            )}
                            <i className="bi bi-people"></i>
                            {group.group_name}
                          </span>
                          {group.is_posted ? (
                            <span style={{
                              background: '#198754',
                              color: 'white',
                              padding: '4px 10px',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              <i className="bi bi-check-lg me-1"></i>
                              פורסם
                            </span>
                          ) : isExcluded ? (
                            <span style={{
                              background: '#ffc107',
                              color: '#856404',
                              padding: '4px 10px',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              <i className="bi bi-x-lg me-1"></i>
                              הוסר
                            </span>
                          ) : (
                            <span style={{
                              background: '#e9ecef',
                              color: '#6c757d',
                              padding: '4px 10px',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              לפרסום
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <a
            href={listing.share_uri}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-cancel"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}
          >
            <i className="bi bi-facebook"></i>
            פתח בפייסבוק
          </a>
          <button type="button" className="btn-submit" onClick={onClose}>
            סגור
          </button>
        </div>
      </div>
    </div>
  )
}

// Create New Listing Modal - posts directly to Facebook
function CreateFacebookListingModal({
  show,
  onClose,
  onSuccess,
  prefillData
}: {
  show: boolean
  onClose: () => void
  onSuccess: () => void
  prefillData?: {
    title?: string
    description?: string
    photos?: string[]
    source_post_id?: string
    source_campaign_id?: string
  } | null
}) {
  const [profiles, setProfiles] = useState<FacebookProfile[]>([])
  const [selectedProfileId, setSelectedProfileId] = useState('')
  const [listingType, setListingType] = useState<'item' | 'vehicle' | 'rental' | 'home_sale'>('item')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [price, setPrice] = useState('0')
  const [condition, setCondition] = useState('used_good')
  const [itemBrand, setItemBrand] = useState('')
  const [itemColor, setItemColor] = useState('')
  const [itemSize, setItemSize] = useState('')
  const [location, setLocation] = useState<{ lat: number; lng: number; city: string }>({ lat: 0, lng: 0, city: '' })
  const [photos, setPhotos] = useState<string[]>([])
  const [uploadingPhotos, setUploadingPhotos] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingProfiles, setLoadingProfiles] = useState(true)
  const [error, setError] = useState('')
  const [analyzing, setAnalyzing] = useState(false)
  const [aiSuggestions, setAiSuggestions] = useState<{
    listing_type: string
    suggested_category: string
    suggested_category_id?: string
    sub_category_id?: string
    title: string
    price: number
    description: string
    confidence: number
    item_condition?: string
    bedrooms?: number
    bathrooms?: number
    square_meters?: number
    property_type?: string
    vehicle_year?: number
    vehicle_mileage?: number
  } | null>(null)
  const [skipCategoryReset, setSkipCategoryReset] = useState(false)

  // Vehicle-specific fields
  const [vehicleMake, setVehicleMake] = useState('')
  const [vehicleModel, setVehicleModel] = useState('')
  const [vehicleYear, setVehicleYear] = useState('')
  const [vehicleMileage, setVehicleMileage] = useState('')

  // Rental/Property-specific fields
  const [propertyType, setPropertyType] = useState('apartment')
  const [bedrooms, setBedrooms] = useState('')
  const [bathrooms, setBathrooms] = useState('')
  const [squareMeters, setSquareMeters] = useState('')

  // Category (Facebook marketplace category IDs)
  const [categoryId, setCategoryId] = useState('')
  const [autoReplyMessage, setAutoReplyMessage] = useState('')

  // Facebook Marketplace category options by listing type
  // Category IDs from captured successful Facebook requests - VERIFIED IDs ONLY
  const categoryOptions: Record<string, {id: string, name: string}[]> = {
    item: [
      // Verified from captured requests
      { id: '1567543000236608', name: 'תיקים ומזוודות (Bags & Luggage)' },
      { id: '1583634935226685', name: 'ריהוט (Furniture)' },
      { id: '678754142233400', name: 'מכשירי חשמל למטבח (Kitchen Appliances)' },
      { id: '686977074745292', name: 'משחקי וידאו (Video Games)' },
      { id: '214968118845643', name: 'תכשיטים (Jewelry)' },
      { id: '1266429133383966', name: 'תיקים ואביזרים (Bags & Accessories)' },
      { id: '1670493229902393', name: 'כללי (General)' },
      { id: '1569171756675761', name: 'לבית (Household)' },
      { id: '613858625416355', name: 'ספרים/סרטים/מוזיקה (Books/Movies/Music)' },
    ],
    vehicle: [
      { id: '807311116002614', name: 'מכוניות' }  // Verified working from captured request
    ],
    rental: [
      { id: '1468271819871448', name: 'נכס להשכרה' }  // Verified working from captured request
    ],
    home_sale: [
      { id: '821056594720130', name: 'נכס למכירה' }  // Home sales category from Facebook
    ]
  }

  // Load profiles on mount
  useEffect(() => {
    if (show) {
      loadProfiles()
    }
  }, [show])

  // Populate form with prefill data when provided
  useEffect(() => {
    if (show && prefillData) {
      // Set title and description from post data
      if (prefillData.title) setTitle(prefillData.title)
      if (prefillData.description) setDescription(prefillData.description)
      if (prefillData.photos && prefillData.photos.length > 0) setPhotos(prefillData.photos)
      // Default to 'item' type and 'General' category for prefilled posts
      setListingType('item')
      setCategoryId('1670493229902393') // General category

      // Trigger AI analysis
      analyzePostContent(prefillData.description || prefillData.title || '')
    }
  }, [show, prefillData])

  // AI analysis function
  const analyzePostContent = async (content: string) => {
    if (!content) return

    setAnalyzing(true)
    console.log('[CreateFacebookListingModal] Analyzing content:', content.substring(0, 100))
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/analyze-post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}').api_token}`
        },
        body: JSON.stringify({
          post_content: content,
          image_urls: prefillData?.photos || []
        })
      })

      console.log('[CreateFacebookListingModal] API response status:', response.status)
      if (response.ok) {
        const suggestions = await response.json()
        console.log('[CreateFacebookListingModal] AI suggestions:', suggestions)
        setAiSuggestions(suggestions)

        // Apply AI suggestions
        // Set skip flag before changing listing type so category doesn't get reset
        if (suggestions.suggested_category_id) {
          setSkipCategoryReset(true)
        }
        if (suggestions.listing_type) setListingType(suggestions.listing_type)
        if (suggestions.title) setTitle(suggestions.title)
        if (suggestions.description) setDescription(suggestions.description)
        setPrice(suggestions.price > 0 ? suggestions.price.toString() : '0')

        // Apply item condition
        if (suggestions.item_condition) setCondition(suggestions.item_condition)

        // Apply real estate fields
        if (suggestions.bedrooms) setBedrooms(suggestions.bedrooms.toString())
        if (suggestions.bathrooms) setBathrooms(suggestions.bathrooms.toString())
        if (suggestions.square_meters) setSquareMeters(suggestions.square_meters.toString())
        if (suggestions.property_type) setPropertyType(suggestions.property_type)

        // Apply vehicle fields
        if (suggestions.vehicle_year) setVehicleYear(suggestions.vehicle_year.toString())
        if (suggestions.vehicle_mileage) setVehicleMileage(suggestions.vehicle_mileage.toString())

        // Set category after listing type with a small delay to ensure it's applied
        if (suggestions.suggested_category_id) {
          setTimeout(() => setCategoryId(suggestions.suggested_category_id), 100)
        }
      }
    } catch (err) {
      console.error('[CreateFacebookListingModal] AI analysis failed:', err)
    } finally {
      setAnalyzing(false)
    }
  }

  // Reset category when listing type changes (unless AI is setting it)
  // Auto-select if there's only one category option for the type
  useEffect(() => {
    if (skipCategoryReset) {
      setSkipCategoryReset(false)
      return
    }
    const options = categoryOptions[listingType]
    if (options && options.length === 1) {
      setCategoryId(options[0].id)
    } else {
      setCategoryId('')
    }
  }, [listingType])

  const loadProfiles = async () => {
    setLoadingProfiles(true)
    try {
      const response = await getFacebookProfiles()
      setProfiles(response.profiles || [])
      // Auto-select first profile
      if (response.profiles && response.profiles.length > 0) {
        setSelectedProfileId(response.profiles[0].id)
      }
    } catch (err) {
      console.error('Failed to load profiles:', err)
    } finally {
      setLoadingProfiles(false)
    }
  }

  const handlePhotoUpload = async (files: FileList) => {
    if (files.length === 0) return
    setUploadingPhotos(true)
    try {
      const { uploadPhotos } = await import('./core/_requests')
      const result = await uploadPhotos(Array.from(files))
      setPhotos([...photos, ...result.urls])
    } catch (err) {
      console.error('Failed to upload photos:', err)
      setError('שגיאה בהעלאת התמונות')
    } finally {
      setUploadingPhotos(false)
    }
  }

  const removePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index))
  }

  const scrollToField = (fieldId: string, errorMsg: string) => {
    setError(errorMsg)
    const el = document.getElementById(fieldId)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el.style.transition = 'box-shadow 0.3s'
      el.style.boxShadow = '0 0 0 2px #dc3545'
      setTimeout(() => { el.style.boxShadow = '' }, 3000)
    }
  }

  const handleSubmit = async () => {
    // Validation with scroll-to-error
    if (!selectedProfileId) {
      scrollToField('cfb-field-profile', 'יש לבחור פרופיל')
      return
    }
    if (listingType === 'item' && !categoryId) {
      scrollToField('cfb-field-category', 'יש לבחור קטגוריה')
      return
    }
    if (!title.trim()) {
      scrollToField('cfb-field-title', 'יש להזין כותרת')
      return
    }
    if (!price || parseFloat(price) <= 0) {
      scrollToField('cfb-field-price', 'יש להזין מחיר תקין')
      return
    }
    if (!location.city) {
      scrollToField('cfb-field-location', 'יש לבחור מיקום')
      return
    }
    if (photos.length === 0) {
      scrollToField('cfb-field-photos', 'יש להעלות לפחות תמונה אחת')
      return
    }

    // Vehicle-specific validation
    if (listingType === 'vehicle') {
      if (!vehicleMake.trim()) {
        scrollToField('cfb-field-vehicle-make', 'יש להזין יצרן הרכב')
        return
      }
      if (!vehicleModel.trim()) {
        scrollToField('cfb-field-vehicle-model', 'יש להזין דגם הרכב')
        return
      }
      if (!vehicleYear) {
        scrollToField('cfb-field-vehicle-year', 'יש להזין שנת ייצור')
        return
      }
    }

    setLoading(true)
    setError('')

    try {
      // Find category name from categoryOptions
      const selectedCategory = categoryOptions[listingType]?.find(cat => cat.id === categoryId)

      // Build request body based on listing type
      const requestBody: any = {
        profile_id: selectedProfileId,
        listing_type: listingType,
        title: title.trim(),
        description: description.trim(),
        price: parseFloat(price),
        category_id: categoryId,
        category_name: selectedCategory?.name || '',
        city: location.city,
        latitude: location.lat,
        longitude: location.lng,
        photo_urls: photos,
        source_post_id: prefillData?.source_post_id,
        source_campaign_id: prefillData?.source_campaign_id
      }

      // Add auto-reply message if provided
      if (autoReplyMessage.trim()) {
        requestBody.auto_reply_message = autoReplyMessage.trim()
      }

      // Add type-specific fields
      if (listingType === 'item') {
        requestBody.condition = condition
        if (itemBrand.trim()) requestBody.brand = itemBrand.trim()
        if (itemColor.trim()) requestBody.color = itemColor.trim()
        if (itemSize.trim()) requestBody.size = itemSize.trim()
      } else if (listingType === 'vehicle') {
        requestBody.vehicle_data = {
          make: vehicleMake.trim(),
          model: vehicleModel.trim(),
          year: parseInt(vehicleYear),
          mileage: vehicleMileage.trim()
        }
      } else if (listingType === 'rental' || listingType === 'home_sale') {
        requestBody.property_data = {
          property_type: propertyType,
          bedrooms: bedrooms ? parseInt(bedrooms) : undefined,
          bathrooms: bathrooms ? parseInt(bathrooms) : undefined,
          square_meters: squareMeters ? parseInt(squareMeters) : undefined
        }
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/synced-listings/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}').api_token}`
        },
        body: JSON.stringify(requestBody)
      })

      const result = await response.json()

      if (result.success) {
        // Reset form
        setListingType('item')
        setTitle('')
        setDescription('')
        setPrice('')
        setCondition('used_good')
        setItemBrand('')
        setItemColor('')
        setItemSize('')
        setCategoryId('')
        setLocation({ lat: 0, lng: 0, city: '' })
        setPhotos([])
        setVehicleMake('')
        setVehicleModel('')
        setVehicleYear('')
        setVehicleMileage('')
        setPropertyType('apartment')
        setBedrooms('')
        setBathrooms('')
        setSquareMeters('')
        setAutoReplyMessage('')
        setError('')
        onSuccess()
        onClose()
      } else {
        // Display Facebook error if available
        setError(result.facebook_error || result.error || 'שגיאה ביצירת המודעה')
      }
    } catch (err: any) {
      console.error('Error creating listing:', err)
      setError(err.response?.data?.error || err.message || 'שגיאה ביצירת המודעה')
    } finally {
      setLoading(false)
    }
  }

  if (!show) return null

  return (
    <div className="marketplace-modal-overlay">
      <div className="marketplace-modal" dir="rtl" style={{ maxWidth: '600px', maxHeight: '90vh' }}>
        <div className="modal-header">
          <div>
            <h2>יצירת מודעה חדשה</h2>
            <p>המודעה תפורסם ישירות בפייסבוק מרקטפלייס</p>
          </div>
          <button className="close-btn" onClick={onClose}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* AI Analysis Status */}
          {analyzing && (
            <div style={{
              background: '#cce5ff',
              border: '1px solid #b8daff',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              color: '#004085',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span className="spinner" style={{ width: '20px', height: '20px' }}></span>
              <div>
                <strong>מנתח את הפוסט...</strong>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>מזהה סוג מודעה, מחיר, כותרת וקטגוריה</div>
              </div>
            </div>
          )}

          {/* AI Suggestions Result */}
          {aiSuggestions && !analyzing && (
            <div style={{
              background: aiSuggestions.confidence > 0.5 ? '#d4edda' : '#fff3cd',
              border: `1px solid ${aiSuggestions.confidence > 0.5 ? '#c3e6cb' : '#ffc107'}`,
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              color: aiSuggestions.confidence > 0.5 ? '#155724' : '#856404'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <i className={`bi ${aiSuggestions.confidence > 0.5 ? 'bi-magic' : 'bi-exclamation-triangle'}`} style={{ fontSize: '20px' }}></i>
                <div style={{ flex: 1 }}>
                  <strong>
                    {aiSuggestions.confidence > 0.5 ? 'זוהו הפרטים הבאים:' : 'לא הצלחנו לזהות את כל הפרטים'}
                  </strong>
                  <div style={{ marginTop: '8px', fontSize: '13px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                    <span>מחיר: <strong>{aiSuggestions.price > 0 ? `₪${aiSuggestions.price.toLocaleString()}` : 'לא זוהה'}</strong></span>
                    <span>קטגוריה: <strong>{aiSuggestions.suggested_category}</strong></span>
                    <span>ביטחון: <strong>{Math.round(aiSuggestions.confidence * 100)}%</strong></span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="error-alert" style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              color: '#856404',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Profile Selector */}
          <div className="form-section">
            <div className="form-group" id="cfb-field-profile">
              <label>
                <i className="bi bi-person-circle me-2"></i>
                פרופיל לפרסום *
              </label>
              {loadingProfiles ? (
                <div style={{ padding: '12px', textAlign: 'center', color: '#a1a5b7' }}>
                  <span className="spinner" style={{ width: '16px', height: '16px' }}></span> טוען פרופילים...
                </div>
              ) : profiles.length === 0 ? (
                <div style={{ padding: '12px', textAlign: 'center', color: '#dc3545' }}>
                  אין פרופילים מחוברים
                </div>
              ) : (
                <select
                  value={selectedProfileId}
                  onChange={(e) => setSelectedProfileId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e4e6ef',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  {profiles.map(profile => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name} {!profile.is_logged_in && '(לא מחובר)'}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Listing Type Selector */}
            <div className="form-group">
              <label>
                <i className="bi bi-tag me-2"></i>
                סוג מודעה *
              </label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '8px'
              }}>
                {[
                  { value: 'item', label: 'פריט', icon: 'bi-box-seam', color: '#6c757d' },
                  { value: 'vehicle', label: 'רכב', icon: 'bi-car-front', color: '#0d6efd' },
                  { value: 'rental', label: 'השכרה', icon: 'bi-house', color: '#6f42c1' },
                  { value: 'home_sale', label: 'נדל"ן', icon: 'bi-building', color: '#198754' }
                ].map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setListingType(type.value as any)}
                    style={{
                      padding: '12px 8px',
                      border: '2px solid',
                      borderColor: listingType === type.value ? type.color : '#e4e6ef',
                      borderRadius: '8px',
                      background: listingType === type.value ? `${type.color}15` : 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <i className={`bi ${type.icon}`} style={{
                      fontSize: '20px',
                      color: listingType === type.value ? type.color : '#5e6278'
                    }}></i>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: listingType === type.value ? 600 : 400,
                      color: listingType === type.value ? type.color : '#5e6278'
                    }}>{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Category Selector - only for items, others auto-pick */}
            {listingType === 'item' && (
              <div className="form-group" id="cfb-field-category">
                <label>
                  <i className="bi bi-grid me-2"></i>
                  קטגוריה *
                </label>
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e4e6ef',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">בחר קטגוריה...</option>
                  {categoryOptions[listingType]?.map(cat => (
                    <option key={cat.id + cat.name} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Title */}
            <div className="form-group" id="cfb-field-title">
              <label>כותרת *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={
                  listingType === 'vehicle' ? 'לדוגמה: טויוטה קורולה 2020' :
                  listingType === 'rental' ? 'לדוגמה: דירת 3 חדרים להשכרה' :
                  listingType === 'home_sale' ? 'לדוגמה: דירת 4 חדרים למכירה' :
                  'מה אתה מוכר?'
                }
                maxLength={100}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e4e6ef',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Price */}
            <div className="form-group" id="cfb-field-price">
              <label>
                {listingType === 'rental' ? 'שכירות חודשית (₪) *' : 'מחיר (₪) *'}
              </label>
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder={listingType === 'rental' ? '3500' : '0'}
                min="0"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e4e6ef',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Description */}
            <div className="form-group">
              <label>תיאור</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="תאר את הפריט שלך..."
                rows={4}
                maxLength={2000}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e4e6ef',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Auto Reply Message */}
            <div className="form-group">
              <label>הודעת תגובה אוטומטית</label>
              <textarea
                value={autoReplyMessage}
                onChange={(e) => setAutoReplyMessage(e.target.value)}
                placeholder="הודעה שתישלח אוטומטית למתעניינים..."
                rows={2}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e4e6ef',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
              <small style={{ color: '#999', fontSize: '12px' }}>אופציונלי - תישלח אוטומטית לכל מי שכותב על המודעה</small>
            </div>

            {/* Item-specific fields */}
            {listingType === 'item' && (
              <>
                <div className="form-group">
                  <label>מצב הפריט *</label>
                  <select
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e4e6ef',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="new">חדש</option>
                    <option value="used_like_new">משומש - כמו חדש</option>
                    <option value="used_good">משומש - מצב טוב</option>
                    <option value="used_fair">משומש - מצב סביר</option>
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label>מותג</label>
                    <input type="text" value={itemBrand} onChange={(e) => setItemBrand(e.target.value)} placeholder="לדוגמה: Apple, Nike" style={{ width: '100%', padding: '10px 12px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '14px' }} />
                  </div>
                  <div className="form-group">
                    <label>צבע</label>
                    <input type="text" value={itemColor} onChange={(e) => setItemColor(e.target.value)} placeholder="לדוגמה: שחור, לבן" style={{ width: '100%', padding: '10px 12px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '14px' }} />
                  </div>
                </div>
                <div className="form-group">
                  <label>מידה</label>
                  <input type="text" value={itemSize} onChange={(e) => setItemSize(e.target.value)} placeholder="לדוגמה: M, 42" style={{ width: '100%', padding: '10px 12px', border: '1px solid #e4e6ef', borderRadius: '8px', fontSize: '14px' }} />
                </div>
              </>
            )}

            {/* Vehicle-specific fields */}
            {listingType === 'vehicle' && (
              <>
                <div style={{
                  background: '#e7f1ff',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px'
                }}>
                  <h6 style={{ margin: 0, color: '#0d6efd', fontSize: '14px' }}>
                    <i className="bi bi-car-front me-2"></i>
                    פרטי הרכב
                  </h6>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group" id="cfb-field-vehicle-make">
                    <label>יצרן *</label>
                    <input
                      type="text"
                      value={vehicleMake}
                      onChange={(e) => setVehicleMake(e.target.value)}
                      placeholder="לדוגמה: טויוטה"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #e4e6ef',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div className="form-group" id="cfb-field-vehicle-model">
                    <label>דגם *</label>
                    <input
                      type="text"
                      value={vehicleModel}
                      onChange={(e) => setVehicleModel(e.target.value)}
                      placeholder="לדוגמה: קורולה"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #e4e6ef',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div className="form-group" id="cfb-field-vehicle-year">
                    <label>שנת ייצור *</label>
                    <input
                      type="number"
                      value={vehicleYear}
                      onChange={(e) => setVehicleYear(e.target.value)}
                      placeholder="2020"
                      min="1900"
                      max="2030"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #e4e6ef',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>קילומטראז׳</label>
                    <input
                      type="text"
                      value={vehicleMileage}
                      onChange={(e) => setVehicleMileage(e.target.value)}
                      placeholder="לדוגמה: 50,000 ק״מ"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #e4e6ef',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Property-specific fields (rental and home_sale) */}
            {(listingType === 'rental' || listingType === 'home_sale') && (
              <>
                <div style={{
                  background: listingType === 'rental' ? '#f3e8ff' : '#d1e7dd',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px'
                }}>
                  <h6 style={{ margin: 0, color: listingType === 'rental' ? '#6f42c1' : '#198754', fontSize: '14px' }}>
                    <i className={`bi ${listingType === 'rental' ? 'bi-house' : 'bi-building'} me-2`}></i>
                    {listingType === 'rental' ? 'פרטי הנכס להשכרה' : 'פרטי הנכס למכירה'}
                  </h6>
                </div>
                <div className="form-group">
                  <label>סוג נכס</label>
                  <select
                    value={propertyType}
                    onChange={(e) => setPropertyType(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e4e6ef',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="apartment">דירה</option>
                    <option value="house">בית פרטי / קוטג׳</option>
                    <option value="penthouse">פנטהאוז</option>
                    <option value="garden_apartment">דירת גן</option>
                    <option value="duplex">דופלקס</option>
                    <option value="condo">קונדו</option>
                    <option value="townhouse">טאון האוס</option>
                    <option value="studio">סטודיו</option>
                    <option value="private_room-shared_room">חדר</option>
                    <option value="land">מגרש</option>
                    <option value="agricultural_lot">מגרש חקלאי</option>
                    <option value="multi-family">מרובה משפחות</option>
                    <option value="office">משרד</option>
                    <option value="retail">חנות</option>
                    <option value="warehouse">מחסן</option>
                    <option value="commercial">מסחרי (כללי)</option>
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label>חדרים</label>
                    <input
                      type="number"
                      value={bedrooms}
                      onChange={(e) => setBedrooms(e.target.value)}
                      placeholder="3"
                      min="0"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #e4e6ef',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>חדרי רחצה</label>
                    <input
                      type="number"
                      value={bathrooms}
                      onChange={(e) => setBathrooms(e.target.value)}
                      placeholder="1"
                      min="0"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #e4e6ef',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>מ״ר</label>
                    <input
                      type="number"
                      value={squareMeters}
                      onChange={(e) => setSquareMeters(e.target.value)}
                      placeholder="80"
                      min="0"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #e4e6ef',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Location - Israeli City Selector */}
            <div className="form-group" id="cfb-field-location">
              <label>
                <i className="bi bi-geo-alt me-2"></i>
                מיקום *
              </label>
              <IsraelCitySelector
                value={location}
                onChange={(loc) => setLocation(loc)}
                placeholder="חפש עיר..."
              />
            </div>

            {/* Photos */}
            <div className="form-group" id="cfb-field-photos">
              <label>
                <i className="bi bi-images me-2"></i>
                תמונות *
              </label>
              <div style={{ marginBottom: '12px' }}>
                <input
                  type="file"
                  id="create-photo-upload"
                  multiple
                  accept="image/*"
                  onChange={(e) => e.target.files && handlePhotoUpload(e.target.files)}
                  style={{ display: 'none' }}
                />
                <label
                  htmlFor="create-photo-upload"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '20px',
                    border: '2px dashed #e4e6ef',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: '#5e6278',
                    transition: 'all 0.2s',
                    background: '#f8f9fa'
                  }}
                >
                  {uploadingPhotos ? (
                    <><span className="spinner" style={{ width: '16px', height: '16px' }}></span> מעלה תמונות...</>
                  ) : (
                    <><i className="bi bi-cloud-arrow-up" style={{ fontSize: '24px' }}></i> לחץ להעלאת תמונות</>
                  )}
                </label>
              </div>
              {photos.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                  gap: '8px'
                }}>
                  {photos.map((url, index) => (
                    <div
                      key={index}
                      style={{
                        position: 'relative',
                        aspectRatio: '1',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid #e4e6ef'
                      }}
                    >
                      <img
                        src={toCdnUrl(url)}
                        alt={`תמונה ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      {index === 0 && (
                        <span style={{
                          position: 'absolute',
                          bottom: '4px',
                          left: '4px',
                          background: '#009ef7',
                          color: 'white',
                          fontSize: '9px',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>ראשית</span>
                      )}
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        style={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          width: '20px',
                          height: '20px',
                          border: 'none',
                          borderRadius: '4px',
                          background: 'rgba(220,53,69,0.9)',
                          color: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px'
                        }}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{
            background: '#e7f1ff',
            border: '1px solid #0d6efd',
            borderRadius: '8px',
            padding: '12px',
            marginTop: '16px',
            fontSize: '13px',
            color: '#0d6efd'
          }}>
            <i className="bi bi-info-circle me-2"></i>
            המודעה תפורסם ישירות בפייסבוק מרקטפלייס דרך הפרופיל שנבחר
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={loading}>
            ביטול
          </button>
          <button
            type="button"
            className="btn-submit"
            onClick={handleSubmit}
            disabled={loading}
            style={{ background: '#1877f2' }}
          >
            {loading ? (
              <><span className="spinner"></span> מפרסם...</>
            ) : (
              <><i className="bi bi-facebook me-2"></i>פרסם בפייסבוק</>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// Main Marketplace Wrapper - Only showing synced Facebook listings
export function MarketplaceWrapper() {
  const { profileId } = useParams<{ profileId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [syncedListings, setSyncedListings] = useState<FacebookSyncedListing[]>([])
  const [loading, setLoading] = useState(true)

  // Profiles for displaying on listings
  const [profiles, setProfiles] = useState<FacebookProfile[]>([])

  // My listings (pending/failed)
  const [myListings, setMyListings] = useState<MyListing[]>([])
  const [loadingMyListings, setLoadingMyListings] = useState(false)
  const [retryingId, setRetryingId] = useState<string | null>(null)

  // Create listing modal
  const [showCreateModal, setShowCreateModal] = useState(false)

  // Prefill data for create modal (from campaign post)
  const [prefillData, setPrefillData] = useState<{
    title?: string
    description?: string
    photos?: string[]
    source_post_id?: string
    source_campaign_id?: string
  } | null>(null)

  // Check for create=true param and prefill data on mount
  useEffect(() => {
    const createParam = searchParams.get('create')
    if (createParam === 'true') {
      // Check for prefill data in localStorage
      const prefillDataStr = localStorage.getItem('marketplace_prefill_data')
      if (prefillDataStr) {
        try {
          const data = JSON.parse(prefillDataStr)
          setPrefillData(data)
          // Clear localStorage after reading
          localStorage.removeItem('marketplace_prefill_data')
        } catch (e) {
          console.error('Failed to parse prefill data:', e)
        }
      }
      // Open the create modal
      setShowCreateModal(true)
      // Clear the create param from URL
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('create')
      setSearchParams(newParams, { replace: true })
    }
  }, [])

  // Synced listing edit modal
  const [showEditSyncedModal, setShowEditSyncedModal] = useState(false)
  const [editingSyncedListing, setEditingSyncedListing] = useState<FacebookSyncedListing | null>(null)

  // Pending listing edit modal
  const [showEditPendingModal, setShowEditPendingModal] = useState(false)
  const [editingPendingListing, setEditingPendingListing] = useState<MyListing | null>(null)
  const [aiFilling, setAiFilling] = useState(false)

  // Pull bedrooms/bathrooms/sqm/property_type from the listing description via /analyze-post
  const aiFillHomeData = async () => {
    if (!editingPendingListing) return
    const text = editingPendingListing.description || editingPendingListing.title || ''
    if (!text.trim()) {
      alert('אין תיאור לפוסט — לא ניתן להשלים עם AI')
      return
    }
    setAiFilling(true)
    try {
      const authToken = localStorage.getItem('kt-auth-react-v')
      const parsed = authToken ? JSON.parse(authToken) : null
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/analyze-post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(parsed?.api_token ? {'Authorization': `Bearer ${parsed.api_token}`} : {}),
        },
        body: JSON.stringify({post_content: text, listing_type: editingPendingListing.listing_type}),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const s = await res.json()
      const merged: any = {...(editingPendingListing.home_data || {})}
      if (s.bedrooms) merged.bedrooms = s.bedrooms
      if (s.bathrooms) merged.bathrooms = s.bathrooms
      if (s.square_meters) merged.square_meters = s.square_meters
      if (s.property_type && !merged.property_type) merged.property_type = s.property_type
      setEditingPendingListing({...editingPendingListing, home_data: merged})
    } catch (err: any) {
      console.error('AI fill failed:', err)
      alert('שגיאה בהשלמה אוטומטית: ' + (err?.message || 'Unknown'))
    } finally {
      setAiFilling(false)
    }
  }

  // Handle pending URL parameter to open listing details or edit modal
  useEffect(() => {
    const pendingId = searchParams.get('pending')
    if (pendingId && myListings.length > 0) {
      const listing = myListings.find(l => l._id === pendingId)
      if (listing) {
        // If the listing has been posted (has facebook_listing_id), try to open details modal
        if (listing.facebook_listing_id && syncedListings.length > 0) {
          const syncedListing = syncedListings.find(s => s.facebook_listing_id === listing.facebook_listing_id)
          if (syncedListing) {
            setDetailsListing(syncedListing)
            setDetailsInitialTab('info')
            setShowDetailsModal(true)
            // Update URL with listing param
            const newParams = new URLSearchParams(searchParams)
            newParams.delete('pending')
            newParams.set('listing', syncedListing._id)
            newParams.set('tab', 'info')
            setSearchParams(newParams, { replace: true })
            return
          }
        }
        // Otherwise open the edit modal for pending listing
        setEditingPendingListing(listing)
        setShowEditPendingModal(true)
      }
    }
  }, [myListings, syncedListings, searchParams])

  // Auto-fill rooms via AI when opening a pending property listing without bedrooms set.
  // Only runs once per listing-id (tracked via ref) so it doesn't loop.
  const aiFilledFor = useState<Set<string>>(() => new Set())[0]
  useEffect(() => {
    if (!showEditPendingModal || !editingPendingListing) return
    const id = editingPendingListing._id
    if (aiFilledFor.has(id)) return
    const isProperty = editingPendingListing.listing_type === 'home_sale' || editingPendingListing.listing_type === 'home_rent' || editingPendingListing.listing_type === 'rental'
    if (!isProperty) return
    if (editingPendingListing.home_data?.bedrooms) return
    aiFilledFor.add(id)
    aiFillHomeData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditPendingModal, editingPendingListing?._id])

  // Listing details modal
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [detailsListing, setDetailsListing] = useState<FacebookSyncedListing | null>(null)
  const [detailsInitialTab, setDetailsInitialTab] = useState<'info' | 'comments' | 'messages' | 'groups'>('info')

  // Pagination
  const [syncedPage, setSyncedPage] = useState(1)
  const [syncedTotalPages, setSyncedTotalPages] = useState(1)

  // URL-based modal state helpers
  const openDetailsFromUrl = (listingId: string, tab?: string) => {
    const listing = syncedListings.find(l => l._id === listingId)
    if (listing) {
      setDetailsListing(listing)
      setDetailsInitialTab((tab as any) || 'info')
      setShowDetailsModal(true)
    }
  }

  const updateUrlForDetails = (listingId: string | null, tab?: string) => {
    if (listingId) {
      const params = new URLSearchParams(searchParams)
      params.set('listing', listingId)
      if (tab) params.set('tab', tab)
      setSearchParams(params, { replace: true })
    } else {
      const params = new URLSearchParams(searchParams)
      params.delete('listing')
      params.delete('tab')
      setSearchParams(params, { replace: true })
    }
  }

  // Handle URL params on load and when listings change
  useEffect(() => {
    const listingId = searchParams.get('listing')
    const tab = searchParams.get('tab')
    if (listingId && syncedListings.length > 0) {
      openDetailsFromUrl(listingId, tab || undefined)
    }
  }, [syncedListings, searchParams])

  useEffect(() => {
    loadSyncedData()
    loadMyListings()
    loadProfiles()
  }, [syncedPage, profileId])

  const loadProfiles = async () => {
    try {
      const response = await getFacebookProfiles()
      setProfiles(response.profiles || [])
    } catch (err) {
      console.error('Failed to load profiles:', err)
    }
  }

  const loadSyncedData = async () => {
    setLoading(true)
    try {
      const response = await getSyncedListings({
        page: syncedPage,
        limit: 12,
        ...(profileId ? { profile_id: profileId } : {})
      })
      setSyncedListings(response.listings)
      setSyncedTotalPages(response.pagination?.pages || 1)

      // Update the details modal listing if it's open
      if (detailsListing) {
        const updatedListing = response.listings.find(l => l._id === detailsListing._id)
        if (updatedListing) {
          setDetailsListing(updatedListing)
        }
      }
    } catch (err) {
      console.error('Failed to load synced listings:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadMyListings = async () => {
    setLoadingMyListings(true)
    try {
      console.log('Loading my listings...')
      const response = await getMyListings({ status: 'all' })
      console.log('My listings response:', response)
      // Only show pending and failed listings
      // Held listings (needs_input) show as failed, with the reason in Hebrew, so
      // the customer can fix and resend them.
      const heldReason = (msg: string) => {
        const m = String(msg || '')
        const out: string[] = []
        const city = m.match(/city "([^"]+)" not in the ad text, which names ([^;]+)/)
        if (city) out.push(`העיר "${city[1]}" לא מופיעה בטקסט, שמזכיר ${city[2].trim()} — יש לבחור את העיר הנכונה`)
        if (/no city on the listing|no coordinates for city/.test(m)) out.push('יש לבחור עיר')
        if (/description is empty/.test(m)) out.push('התיאור ריק (רק שורת טלפון) — יש להוסיף תיאור')
        if (/listed as rent but the ad text says for sale/.test(m)) out.push('המודעה מסומנת להשכרה אבל הטקסט אומר למכירה')
        if (/listed as sale but the ad text says for rent/.test(m)) out.push('המודעה מסומנת למכירה אבל הטקסט אומר להשכרה')
        return 'המודעה לא פורסמה: ' + (out.length ? out.join(' · ') : m.replace(/^needs_input:\s*/, ''))
      }
      const filtered = response.listings
        .map(l => l.status === 'needs_input' ? { ...l, status: 'failed', error_message: heldReason(l.error_message) } : l)
        .filter(l => l.status === 'pending' || l.status === 'failed')
      console.log('Filtered listings (pending/failed):', filtered)
      setMyListings(filtered)
    } catch (err) {
      console.error('Failed to load my listings:', err)
    } finally {
      setLoadingMyListings(false)
    }
  }

  const handleRetry = async (listingId: string) => {
    setRetryingId(listingId)
    try {
      await retryListing(listingId)
      // Reload listings after a short delay
      setTimeout(() => {
        loadMyListings()
        loadSyncedData()
      }, 2000)
    } catch (err) {
      console.error('Failed to retry listing:', err)
    } finally {
      setRetryingId(null)
    }
  }

  const handleDeleteMyListing = async (listingId: string) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק מודעה זו?')) return
    try {
      await deleteMyListing(listingId)
      loadMyListings()
    } catch (err) {
      console.error('Failed to delete listing:', err)
    }
  }

  const handleDeleteSyncedListing = async (listingId: string) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק מודעה זו מפייסבוק? פעולה זו לא ניתנת לביטול.')) return
    try {
      await deleteSyncedListing(listingId)
      // Reload listings after a short delay to allow Facebook to process
      setTimeout(() => {
        loadSyncedData()
      }, 2000)
    } catch (err) {
      console.error('Failed to delete synced listing:', err)
    }
  }

  const handleMarkSold = async (listingId: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/synced-listings/${listingId}/mark-sold`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}').api_token}`
        }
      })
      const result = await response.json()
      if (result.success) {
        loadSyncedData()
      }
    } catch (err) {
      console.error('Failed to mark sold:', err)
    }
  }

  const handleMarkAvailable = async (listingId: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/synced-listings/${listingId}/mark-available`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}').api_token}`
        }
      })
      const result = await response.json()
      if (result.success) {
        loadSyncedData()
      }
    } catch (err) {
      console.error('Failed to mark available:', err)
    }
  }

  // Calculate stats
  const stats = {
    total: syncedListings.length,
    active: syncedListings.filter(l => l.is_live && !l.is_sold).length,
    pending: syncedListings.filter(l => l.is_pending).length,
    sold: syncedListings.filter(l => l.is_sold).length,
    views: syncedListings.reduce((sum, l) => sum + (l.views_count || 0), 0),
    messages: syncedListings.reduce((sum, l) => sum + (l.messages_count || l.conversations?.length || 0), 0)
  }

  const currentProfile = profileId ? profiles.find(p => p.id === profileId) : null

  return (
    <div className="mp-page">
      {/* Header Row */}
      <div className="mp-header-row">
        {profileId ? (
          <div className="d-flex align-items-center gap-3">
            <Link to="/marketplace" className="btn btn-sm btn-light-primary d-flex align-items-center gap-1">
              <i className="bi bi-arrow-right"></i>
              חזרה למרקטפלייס
            </Link>
            {currentProfile && (
              <div className="d-flex align-items-center gap-3">
                {currentProfile.profile_picture && (
                  <img
                    src={currentProfile.profile_picture}
                    alt={currentProfile.name}
                    style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                  />
                )}
                <div>
                  <h1 className="mp-header__title" style={{ margin: 0 }}>{currentProfile.name}</h1>
                  <p className="mp-header__subtitle" style={{ margin: 0 }}>מודעות הפרופיל</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mp-header">
            <h1 className="mp-header__title">מרקטפלייס</h1>
            <p className="mp-header__subtitle">ניהול המודעות שלך בפייסבוק</p>
          </div>
        )}
        <button className="mp-create-btn" onClick={() => setShowCreateModal(true)}>
          <i className="bi bi-plus-lg"></i>
          מודעה חדשה
        </button>
      </div>

      {/* Compact Stats Bar */}
      <StatsBar stats={stats} loading={loading} />

      {/* Facebook Profiles Sync Section - only on main marketplace page */}
      {!profileId && (
        <div className="mb-4">
          <FacebookProfilesSync />
        </div>
      )}

      {/* Pending/Failed Listings Section */}
      {myListings.length > 0 && (
        <div className="mp-pending-section">
          <div className="mp-pending-section__header">
            <i className="bi bi-hourglass-split"></i>
            <span>מודעות בהמתנה / נכשלו ({myListings.length})</span>
            <button onClick={loadMyListings} disabled={loadingMyListings} className="mp-pending-section__refresh">
              <i className={`bi bi-arrow-clockwise ${loadingMyListings ? 'spin' : ''}`}></i>
            </button>
          </div>

          <div className="mp-pending-section__list">
            {myListings.map((listing) => (
              <div key={listing._id} className={`mp-pending-item mp-pending-item--${listing.status}`}>
                {listing.photo_urls?.[0] && (
                  <img src={toCdnUrl(listing.photo_urls[0])} alt={listing.title} className="mp-pending-item__image" />
                )}
                <div className="mp-pending-item__info">
                  <div className="mp-pending-item__title">{listing.title}</div>
                  <div className="mp-pending-item__price">₪{listing.price?.toLocaleString()}</div>
                  {listing.status === 'failed' && listing.error_message && (
                    <div className="mp-pending-item__error">
                      {listing.error_message.includes('מגבלת פרסום') ? (
                        <><i className="bi bi-clock-history"></i> מגבלת פרסום - נסה מאוחר יותר</>
                      ) : (
                        <><i className="bi bi-exclamation-triangle-fill"></i> {listing.error_message}</>
                      )}
                    </div>
                  )}
                </div>
                <span className={`mp-pending-item__badge mp-pending-item__badge--${listing.status}`}>
                  {listing.status === 'pending' ? 'בהמתנה' : 'נכשל'}
                </span>
                <div className="mp-pending-item__actions">
                  <button
                    onClick={() => {
                      setEditingPendingListing(listing)
                      setShowEditPendingModal(true)
                      const params = new URLSearchParams(searchParams)
                      params.set('pending', listing._id)
                      setSearchParams(params, { replace: true })
                    }}
                    className="mp-pending-item__edit"
                    title="עריכה"
                  >
                    <i className="bi bi-pencil"></i>
                  </button>
                  {listing.status === 'failed' && (
                    <button onClick={() => handleRetry(listing._id)} disabled={retryingId === listing._id} className="mp-pending-item__retry">
                      {retryingId === listing._id ? <i className="bi bi-arrow-clockwise spin"></i> : <i className="bi bi-arrow-repeat"></i>}
                    </button>
                  )}
                  <button onClick={() => handleDeleteMyListing(listing._id)} className="mp-pending-item__delete">
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Listings Grid */}
      <ListingGrid loading={loading}>
        {syncedListings.length === 0 ? (
          <EmptyState
            icon="facebook"
            title="אין מודעות מסונכרנות"
            description="סנכרן את הפרופיל שלך למעלה כדי לראות את המודעות מפייסבוק"
          />
        ) : (
          syncedListings.map((listing) => (
            <ListingCard
              key={listing._id}
              listing={listing}
              profiles={profiles}
              onView={() => {
                setDetailsInitialTab('info')
                setDetailsListing(listing)
                setShowDetailsModal(true)
                updateUrlForDetails(listing._id, 'info')
              }}
              onEdit={() => {
                setEditingSyncedListing(listing)
                setShowEditSyncedModal(true)
              }}
              onDelete={() => handleDeleteSyncedListing(listing._id)}
              onMarkSold={() => handleMarkSold(listing._id)}
              onMarkAvailable={() => handleMarkAvailable(listing._id)}
              onViewMessages={() => {
                setDetailsInitialTab('messages')
                setDetailsListing(listing)
                setShowDetailsModal(true)
                updateUrlForDetails(listing._id, 'messages')
              }}
            />
          ))
        )}
      </ListingGrid>

      {/* Pagination */}
      {syncedTotalPages > 1 && (
        <div className="pagination" style={{ marginTop: '20px' }}>
          <button
            disabled={syncedPage === 1}
            onClick={() => setSyncedPage(p => p - 1)}
          >
            <i className="bi bi-chevron-right"></i>
          </button>
          <span>עמוד {syncedPage} מתוך {syncedTotalPages}</span>
          <button
            disabled={syncedPage === syncedTotalPages}
            onClick={() => setSyncedPage(p => p + 1)}
          >
            <i className="bi bi-chevron-left"></i>
          </button>
        </div>
      )}

      {/* Floating Action Button for Mobile */}
      <button className="mp-fab" onClick={() => setShowCreateModal(true)} aria-label="מודעה חדשה">
        <i className="bi bi-plus-lg"></i>
      </button>

      {/* Edit Synced Listing Modal */}
      <EditSyncedListingModal
        show={showEditSyncedModal}
        listing={editingSyncedListing}
        onClose={() => {
          setShowEditSyncedModal(false)
          setEditingSyncedListing(null)
        }}
        onSuccess={loadSyncedData}
      />

      {/* Listing Details Modal */}
      <ListingDetailsModal
        show={showDetailsModal}
        listing={detailsListing}
        onClose={() => {
          setShowDetailsModal(false)
          setDetailsListing(null)
          setDetailsInitialTab('info')
          updateUrlForDetails(null)
        }}
        onUpdate={loadSyncedData}
        initialTab={detailsInitialTab}
      />

      {/* Create New Listing Modal */}
      <CreateFacebookListingModal
        show={showCreateModal}
        onClose={() => {
          setShowCreateModal(false)
          setPrefillData(null)
        }}
        onSuccess={loadSyncedData}
        prefillData={prefillData}
      />

      {/* Edit Pending Listing Modal */}
      {showEditPendingModal && editingPendingListing && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" style={{ direction: 'rtl' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingPendingListing.status === 'failed' ? '❌ מודעה נכשלה' : '⏳ מודעה בהמתנה'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowEditPendingModal(false)
                    setEditingPendingListing(null)
                    const params = new URLSearchParams(searchParams)
                    params.delete('pending')
                    setSearchParams(params, { replace: true })
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {/* Status Badge */}
                <div className="mb-3">
                  <span
                    className={`badge ${editingPendingListing.status === 'failed' ? 'bg-danger' : 'bg-warning'}`}
                    style={{ fontSize: '14px', padding: '8px 16px' }}
                  >
                    {editingPendingListing.status === 'failed' ? '❌ נכשל' : '⏳ בהמתנה לפרסום'}
                  </span>
                </div>

                {/* Error Message - Prominent Display */}
                {editingPendingListing.status === 'failed' && (
                  <div className={`alert ${editingPendingListing.error_message?.includes('מגבלת פרסום') ? 'alert-warning' : 'alert-danger'} mb-4`} style={{ direction: 'rtl', textAlign: 'right' }}>
                    {editingPendingListing.error_message?.includes('מגבלת פרסום') ? (
                      <>
                        <strong>
                          <i className="bi bi-clock-history me-2"></i>הגעת למגבלת פרסום
                        </strong>
                        <div style={{ marginTop: '8px', fontSize: '14px' }}>
                          פייסבוק מגבילה את מספר המודעות שניתן ליצור. נסה שוב מאוחר יותר.
                        </div>
                      </>
                    ) : (
                      <>
                        <strong>
                          <i className="bi bi-exclamation-triangle-fill me-2"></i>סיבת הכישלון:
                        </strong>
                        <div style={{ marginTop: '8px', fontFamily: 'monospace', fontSize: '13px', whiteSpace: 'pre-wrap', direction: 'ltr', textAlign: 'left' }}>
                          {editingPendingListing.error_message || 'לא נשמרה הודעת שגיאה. ייתכן שהפרסום עדיין בתהליך או שהשגיאה לא נקלטה.'}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Pending Status Info */}
                {editingPendingListing.status === 'pending' && (
                  <div className="alert alert-warning mb-4">
                    <i className="bi bi-hourglass-split me-2"></i>
                    המודעה ממתינה לפרסום. התהליך יכול לקחת מספר דקות.
                  </div>
                )}

                {/* Editable Form */}
                <div className="row">
                  <div className="col-md-4" id="pending-photo-section">
                    {/* Editable Photos */}
                    <label className="form-label fw-semibold">תמונות *</label>
                    {editingPendingListing.photo_urls?.length > 0 ? (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                        {editingPendingListing.photo_urls.map((url, idx) => (
                          <div key={idx} style={{ position: 'relative', width: idx === 0 ? '100%' : '60px', height: idx === 0 ? 'auto' : '60px' }}>
                            <img
                              src={toCdnUrl(url)}
                              alt={`תמונה ${idx + 1}`}
                              style={{
                                width: '100%',
                                height: idx === 0 ? 'auto' : '60px',
                                borderRadius: '6px',
                                objectFit: 'cover',
                                ...(idx === 0 ? { aspectRatio: '1' } : {})
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newUrls = editingPendingListing.photo_urls.filter((_, i) => i !== idx)
                                setEditingPendingListing({ ...editingPendingListing, photo_urls: newUrls })
                              }}
                              style={{
                                position: 'absolute', top: '2px', right: '2px',
                                background: 'rgba(220,53,69,0.9)', color: '#fff', border: 'none',
                                borderRadius: '50%', width: '20px', height: '20px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '12px', cursor: 'pointer', padding: 0
                              }}
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ width: '100%', aspectRatio: '1', background: '#fff3cd', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed #ffc107', marginBottom: '8px' }}>
                        <i className="bi bi-exclamation-triangle" style={{ fontSize: '32px', color: '#ffc107' }}></i>
                        <span style={{ fontSize: '13px', color: '#856404', marginTop: '4px' }}>חובה להוסיף לפחות תמונה אחת</span>
                      </div>
                    )}
                    <input
                      type="file"
                      id="pending-photo-upload"
                      multiple
                      accept="image/*"
                      onChange={async (e) => {
                        if (!e.target.files || e.target.files.length === 0) return
                        try {
                          const { uploadPhotos } = await import('./core/_requests')
                          const result = await uploadPhotos(Array.from(e.target.files))
                          setEditingPendingListing({
                            ...editingPendingListing,
                            photo_urls: [...(editingPendingListing.photo_urls || []), ...result.urls]
                          })
                        } catch (err) {
                          console.error('Failed to upload photos:', err)
                        }
                        e.target.value = ''
                      }}
                      style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                      <label
                        htmlFor="pending-photo-upload"
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                          padding: '8px', border: '1px dashed #adb5bd', borderRadius: '6px',
                          cursor: 'pointer', fontSize: '13px', color: '#495057', background: '#f8f9fa',
                          flex: 1
                        }}
                      >
                        <i className="bi bi-cloud-arrow-up"></i>
                        הוסף תמונות
                      </label>
                      <button
                        type="button"
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                          padding: '8px 12px', border: '1px dashed #0d6efd', borderRadius: '6px',
                          cursor: 'pointer', fontSize: '13px', color: '#0d6efd', background: '#e7f1ff',
                        }}
                        onClick={async () => {
                          try {
                            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/sync-photos-from-post`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}').api_token}`
                              },
                              body: JSON.stringify({ listing_id: editingPendingListing._id })
                            })
                            const data = await response.json()
                            if (data.success && data.photo_urls?.length > 0) {
                              setEditingPendingListing({ ...editingPendingListing, photo_urls: data.photo_urls })
                              setToast({ message: `סונכרנו ${data.photo_urls.length} תמונות מהפוסט`, type: 'success' })
                            } else {
                              setToast({ message: data.error || 'לא נמצאו תמונות בפוסט', type: 'error' })
                            }
                          } catch (err) {
                            console.error('Error syncing photos from post:', err)
                            setToast({ message: 'שגיאה בסנכרון תמונות', type: 'error' })
                          }
                        }}
                      >
                        <i className="bi bi-arrow-repeat"></i>
                        סנכרן מפוסט
                      </button>
                    </div>
                    <div style={{ marginTop: '16px', fontSize: '13px', color: '#666' }}>
                      <div><i className="bi bi-person me-2"></i>פרופיל: {editingPendingListing.profile_names?.join(', ') || 'לא ידוע'}</div>
                      <div><i className="bi bi-clock me-2"></i>נוצר: {new Date(editingPendingListing.created_at).toLocaleString('he-IL')}</div>
                      {editingPendingListing.retry_count > 0 && (
                        <div><i className="bi bi-arrow-repeat me-2"></i>ניסיונות: {editingPendingListing.retry_count}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-8">
                    {/* Profile Selector */}
                    <div className="mb-3">
                      <label className="form-label">פרופיל לפרסום *</label>
                      <select
                        className="form-select"
                        value={editingPendingListing.profile_ids?.[0] || ''}
                        onChange={(e) => {
                          const selectedProfile = profiles.find(p => p.id === e.target.value)
                          setEditingPendingListing({
                            ...editingPendingListing,
                            profile_ids: [e.target.value],
                            profile_names: selectedProfile ? [selectedProfile.name] : []
                          })
                        }}
                      >
                        <option value="">בחר פרופיל...</option>
                        {profiles.map((profile) => (
                          <option key={profile.id} value={profile.id}>
                            {profile.name}
                          </option>
                        ))}
                      </select>
                      {profiles.length > 0 && editingPendingListing.profile_ids?.[0] && (
                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {(() => {
                            const selectedProfile = profiles.find(p => p.id === editingPendingListing.profile_ids?.[0])
                            return selectedProfile ? (
                              <>
                                <img
                                  src={`https://graph.facebook.com/${selectedProfile.facebook_id}/picture?type=square&access_token=${process.env.REACT_APP_FB_ACCESS_TOKEN}`}
                                  alt=""
                                  style={{ width: '32px', height: '32px', borderRadius: '50%' }}
                                />
                                <span style={{ fontSize: '13px', color: '#666' }}>{selectedProfile.name}</span>
                              </>
                            ) : null
                          })()}
                        </div>
                      )}
                    </div>

                    {/* Listing Type */}
                    <div className="mb-3">
                      <label className="form-label">סוג מודעה</label>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {[
                          { value: 'item', label: '📦 פריט', color: '#6c757d' },
                          { value: 'vehicle', label: '🚗 רכב', color: '#0d6efd' },
                          { value: 'home_rent', label: '🏠 השכרה', color: '#198754' },
                          { value: 'home_sale', label: '🏡 מכירה', color: '#dc3545' }
                        ].map(type => (
                          <button
                            key={type.value}
                            type="button"
                            className={`btn btn-sm ${editingPendingListing.listing_type === type.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setEditingPendingListing({ ...editingPendingListing, listing_type: type.value })}
                          >
                            {type.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Category - only show selector for items */}
                    {editingPendingListing.listing_type === 'item' && (
                    <div className="mb-3">
                      <label className="form-label">קטגוריה</label>
                      {(() => {
                        const categoryOptions = [
                            { id: '1670493229902393', name: 'כללי (General)' },
                            { id: '1792291877663080', name: 'אלקטרוניקה ומחשבים (Electronics & Computers)' },
                            { id: '1583634935226685', name: 'ריהוט (Furniture)' },
                            { id: '1569171756675761', name: 'לבית (Household)' },
                            { id: '678754142233400', name: 'מכשירי חשמל למטבח (Kitchen Appliances)' },
                            { id: '686977074745292', name: 'משחקי וידאו (Video Games)' },
                            { id: '214968118845643', name: 'תכשיטים (Jewelry)' },
                            { id: '1266429133383966', name: 'תיקים ואביזרים (Bags & Accessories)' },
                            { id: '613858625416355', name: 'ספרים/סרטים/מוזיקה (Books/Movies/Music)' },
                            { id: '1567543000236608', name: 'תיקים ומזוודות (Bags & Luggage)' },
                        ]
                        return (
                          <select
                            className="form-select"
                            value={editingPendingListing.category_id || ''}
                            onChange={(e) => {
                              const selectedOpt = categoryOptions.find(c => c.id === e.target.value)
                              setEditingPendingListing({
                                ...editingPendingListing,
                                category_id: e.target.value,
                                category_name: selectedOpt?.name || ''
                              })
                            }}
                          >
                            <option value="">בחר קטגוריה...</option>
                            {categoryOptions.map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                          </select>
                        )
                      })()}
                    </div>
                    )}

                    {/* Title */}
                    <div className="mb-3">
                      <label className="form-label">כותרת</label>
                      {/* Read-only — a title can't be changed on Facebook once the
                          listing is live, so we don't offer to change it here. */}
                      <input
                        type="text"
                        className="form-control"
                        value={editingPendingListing.title || ''}
                        readOnly
                        disabled
                        title="לא ניתן לערוך כותרת — פייסבוק לא מאפשרת לשנות כותרת של מודעה"
                        style={{background: '#f5f6f8', cursor: 'not-allowed'}}
                      />
                    </div>

                    {/* Price */}
                    <div className="mb-3">
                      <label className="form-label">מחיר (₪) *</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editingPendingListing.price || ''}
                        onChange={(e) => setEditingPendingListing({ ...editingPendingListing, price: parseFloat(e.target.value) || 0 })}
                      />
                    </div>

                    {/* Description */}
                    <div className="mb-3">
                      <label className="form-label">תיאור</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={editingPendingListing.description || ''}
                        onChange={(e) => setEditingPendingListing({ ...editingPendingListing, description: e.target.value })}
                      />
                    </div>

                    {/* Location */}
                    <div className="mb-3">
                      <label className="form-label">מיקום *</label>
                      <IsraelCitySelector
                        value={editingPendingListing.latitude && editingPendingListing.longitude
                          ? { lat: editingPendingListing.latitude, lng: editingPendingListing.longitude, city: editingPendingListing.address || '' }
                          : undefined
                        }
                        onChange={(loc) => {
                          setEditingPendingListing({
                            ...editingPendingListing,
                            address: loc.city,
                            latitude: loc.lat,
                            longitude: loc.lng
                          })
                        }}
                        placeholder="חפש עיר..."
                      />
                    </div>

                    {/* Item-specific fields */}
                    {editingPendingListing.listing_type === 'item' && (
                      <>
                        <div className="mb-3">
                          <label className="form-label">מצב הפריט</label>
                          <select
                            className="form-select"
                            value={editingPendingListing.item_attributes?.condition || 'used_good'}
                            onChange={(e) => setEditingPendingListing({
                              ...editingPendingListing,
                              item_attributes: { ...editingPendingListing.item_attributes, condition: e.target.value }
                            })}
                          >
                            <option value="new">חדש</option>
                            <option value="used_like_new">משומש - כמו חדש</option>
                            <option value="used_good">משומש - מצב טוב</option>
                            <option value="used_fair">משומש - מצב סביר</option>
                          </select>
                        </div>
                        <div className="row mb-3">
                          <div className="col-6">
                            <label className="form-label">מותג</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editingPendingListing.item_attributes?.brand || ''}
                              onChange={(e) => setEditingPendingListing({
                                ...editingPendingListing,
                                item_attributes: { ...editingPendingListing.item_attributes, brand: e.target.value }
                              })}
                              placeholder="לדוגמה: Apple, Nike"
                            />
                          </div>
                          <div className="col-6">
                            <label className="form-label">צבע</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editingPendingListing.item_attributes?.color || ''}
                              onChange={(e) => setEditingPendingListing({
                                ...editingPendingListing,
                                item_attributes: { ...editingPendingListing.item_attributes, color: e.target.value }
                              })}
                              placeholder="לדוגמה: שחור, לבן"
                            />
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">מידה</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editingPendingListing.item_attributes?.size || ''}
                            onChange={(e) => setEditingPendingListing({
                              ...editingPendingListing,
                              item_attributes: { ...editingPendingListing.item_attributes, size: e.target.value }
                            })}
                            placeholder="לדוגמה: M, 42"
                          />
                        </div>
                      </>
                    )}

                    {/* Vehicle-specific fields */}
                    {editingPendingListing.listing_type === 'vehicle' && (
                      <>
                        <h6 className="mt-3 mb-3">פרטי הרכב</h6>
                        <div className="mb-3">
                          <label className="form-label">קטגוריית רכב</label>
                          <select
                            className="form-select"
                            value={editingPendingListing.vehicle_data?.vehicleCategory || ''}
                            onChange={(e) => setEditingPendingListing({
                              ...editingPendingListing,
                              vehicle_data: { ...editingPendingListing.vehicle_data, vehicleCategory: e.target.value }
                            })}
                          >
                            <option value="">בחר קטגוריה</option>
                            <option value="car&truck/">רכב / משאית</option>
                            <option value="motorcycle/">אופנוע</option>
                            <option value="powersport/">כלי ספורט ממונע</option>
                            <option value="rv&camper/">קרוואן / קמפר</option>
                            <option value="boat/">סירה</option>
                            <option value="commercial&industrial/">מסחרי / תעשייתי</option>
                            <option value="trailer/">נגרר</option>
                          </select>
                        </div>
                        <div className="row mb-3">
                          <div className="col-6">
                            <label className="form-label">יצרן *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editingPendingListing.vehicle_data?.make || ''}
                              onChange={(e) => setEditingPendingListing({
                                ...editingPendingListing,
                                vehicle_data: { ...editingPendingListing.vehicle_data, make: e.target.value }
                              })}
                              placeholder="לדוגמה: טסלה, טויוטה"
                            />
                          </div>
                          <div className="col-6">
                            <label className="form-label">דגם *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editingPendingListing.vehicle_data?.model || ''}
                              onChange={(e) => setEditingPendingListing({
                                ...editingPendingListing,
                                vehicle_data: { ...editingPendingListing.vehicle_data, model: e.target.value }
                              })}
                              placeholder="לדוגמה: Model 3, Camry"
                            />
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-6">
                            <label className="form-label">שנה *</label>
                            <input
                              type="number"
                              className="form-control"
                              value={editingPendingListing.vehicle_data?.year || ''}
                              onChange={(e) => setEditingPendingListing({
                                ...editingPendingListing,
                                vehicle_data: { ...editingPendingListing.vehicle_data, year: parseInt(e.target.value) }
                              })}
                              placeholder="לדוגמה: 2024"
                              min={1900}
                              max={2030}
                            />
                          </div>
                          <div className="col-6">
                            <label className="form-label">גרסה / Trim</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editingPendingListing.vehicle_data?.trim || ''}
                              onChange={(e) => setEditingPendingListing({
                                ...editingPendingListing,
                                vehicle_data: { ...editingPendingListing.vehicle_data, trim: e.target.value }
                              })}
                              placeholder="לדוגמה: Sport, Limited"
                            />
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-6">
                            <label className="form-label">קילומטראז׳</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editingPendingListing.vehicle_data?.mileage || ''}
                              onChange={(e) => setEditingPendingListing({
                                ...editingPendingListing,
                                vehicle_data: { ...editingPendingListing.vehicle_data, mileage: e.target.value }
                              })}
                              placeholder="לדוגמה: 50000"
                            />
                          </div>
                          <div className="col-6">
                            <label className="form-label">סוג דלק</label>
                            <select
                              className="form-select"
                              value={editingPendingListing.vehicle_data?.fuelType || ''}
                              onChange={(e) => setEditingPendingListing({
                                ...editingPendingListing,
                                vehicle_data: { ...editingPendingListing.vehicle_data, fuelType: e.target.value }
                              })}
                            >
                              <option value="">בחר סוג דלק</option>
                              <option value="GASOLINE">בנזין</option>
                              <option value="DIESEL">דיזל</option>
                              <option value="ELECTRIC">חשמלי</option>
                              <option value="HYBRID">היברידי</option>
                              <option value="FLEX">פלקס</option>
                              <option value="OTHER">אחר</option>
                            </select>
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-6">
                            <label className="form-label">סוג רכב</label>
                            <select
                              className="form-select"
                              value={editingPendingListing.vehicle_data?.vehicleType || ''}
                              onChange={(e) => setEditingPendingListing({
                                ...editingPendingListing,
                                vehicle_data: { ...editingPendingListing.vehicle_data, vehicleType: e.target.value }
                              })}
                            >
                              <option value="">בחר סוג</option>
                              <option value="sedan">סדאן</option>
                              <option value="suv">SUV</option>
                              <option value="truck">טנדר / משאית</option>
                              <option value="coupe">קופה</option>
                              <option value="convertible">קבריולט</option>
                              <option value="hatchback">האצ׳בק</option>
                              <option value="wagon">סטיישן</option>
                              <option value="van">ואן</option>
                              <option value="minivan">מיניוואן</option>
                            </select>
                          </div>
                          <div className="col-6">
                            <label className="form-label">מצב הרכב</label>
                            <select
                              className="form-select"
                              value={editingPendingListing.vehicle_data?.vehicleCondition || ''}
                              onChange={(e) => setEditingPendingListing({
                                ...editingPendingListing,
                                vehicle_data: { ...editingPendingListing.vehicle_data, vehicleCondition: e.target.value }
                              })}
                            >
                              <option value="">בחר מצב</option>
                              <option value="excellent">מצוין</option>
                              <option value="very_good">טוב מאוד</option>
                              <option value="good">טוב</option>
                              <option value="fair">סביר</option>
                              <option value="poor">גרוע</option>
                            </select>
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-6">
                            <label className="form-label">תיבת הילוכים</label>
                            <select
                              className="form-select"
                              value={editingPendingListing.vehicle_data?.isManualTransmission ? 'manual' : 'automatic'}
                              onChange={(e) => setEditingPendingListing({
                                ...editingPendingListing,
                                vehicle_data: { ...editingPendingListing.vehicle_data, isManualTransmission: e.target.value === 'manual' }
                              })}
                            >
                              <option value="automatic">אוטומטית</option>
                              <option value="manual">ידנית</option>
                            </select>
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-6">
                            <label className="form-label">צבע חיצוני</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editingPendingListing.vehicle_data?.exteriorColor || ''}
                              onChange={(e) => setEditingPendingListing({
                                ...editingPendingListing,
                                vehicle_data: { ...editingPendingListing.vehicle_data, exteriorColor: e.target.value }
                              })}
                              placeholder="לדוגמה: שחור, לבן"
                            />
                          </div>
                          <div className="col-6">
                            <label className="form-label">צבע פנימי</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editingPendingListing.vehicle_data?.interiorColor || ''}
                              onChange={(e) => setEditingPendingListing({
                                ...editingPendingListing,
                                vehicle_data: { ...editingPendingListing.vehicle_data, interiorColor: e.target.value }
                              })}
                              placeholder="לדוגמה: שחור, בז׳"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Real estate fields */}
                    {(editingPendingListing.listing_type === 'home_sale' || editingPendingListing.listing_type === 'home_rent' || editingPendingListing.listing_type === 'rental') && (
                      <>
                        <h6 className="mt-3 mb-3">פרטי הנכס</h6>
                        <div className="mb-3">
                          <label className="form-label">סוג נכס</label>
                          <select
                            className="form-select"
                            value={editingPendingListing.home_data?.property_type || ''}
                            onChange={(e) => setEditingPendingListing({
                              ...editingPendingListing,
                              home_data: { ...editingPendingListing.home_data, property_type: e.target.value }
                            })}
                          >
                            <option value="">בחר סוג</option>
                            <option value="apartment">דירה</option>
                            <option value="house">בית פרטי / קוטג׳</option>
                            <option value="penthouse">פנטהאוז</option>
                            <option value="garden_apartment">דירת גן</option>
                            <option value="duplex">דופלקס</option>
                            <option value="condo">קונדו</option>
                            <option value="townhouse">טאון האוס</option>
                            <option value="studio">סטודיו</option>
                            <option value="private_room-shared_room">חדר</option>
                            <option value="land">מגרש</option>
                            <option value="agricultural_lot">מגרש חקלאי</option>
                            <option value="multi-family">מרובה משפחות</option>
                            <option value="office">משרד</option>
                            <option value="retail">חנות</option>
                            <option value="warehouse">מחסן</option>
                            <option value="commercial">מסחרי (כללי)</option>
                          </select>
                        </div>
                        <div className="row mb-3">
                          <div className="col-4">
                            <label className="form-label" style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                              <span>חדרים <span style={{color: '#dc3545'}}>*</span></span>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                style={{padding: '0 8px', fontSize: '11px', lineHeight: '20px'}}
                                onClick={aiFillHomeData}
                                disabled={aiFilling}
                                title="השלמה אוטומטית מהתיאור"
                              >
                                {aiFilling ? (<><i className="bi bi-arrow-clockwise spin me-1"></i>...</>) : (<><i className="bi bi-magic me-1"></i>AI</>)}
                              </button>
                            </label>
                            <input
                              type="number"
                              step="0.5"
                              required
                              className={`form-control ${!editingPendingListing.home_data?.bedrooms ? 'is-invalid' : ''}`}
                              value={editingPendingListing.home_data?.bedrooms ?? ''}
                              onChange={(e) => {
                                const v = e.target.value
                                setEditingPendingListing({
                                  ...editingPendingListing,
                                  home_data: { ...editingPendingListing.home_data, bedrooms: v === '' ? undefined : parseFloat(v) }
                                })
                              }}
                              placeholder="0"
                              min={0}
                            />
                            {!editingPendingListing.home_data?.bedrooms && (
                              <small className="text-danger" style={{fontSize: '11px'}}>שדה חובה</small>
                            )}
                          </div>
                          <div className="col-4">
                            <label className="form-label">חדרי רחצה</label>
                            <input
                              type="number"
                              step="0.5"
                              className="form-control"
                              value={editingPendingListing.home_data?.bathrooms ?? ''}
                              onChange={(e) => {
                                const v = e.target.value
                                setEditingPendingListing({
                                  ...editingPendingListing,
                                  home_data: { ...editingPendingListing.home_data, bathrooms: v === '' ? undefined : parseFloat(v) }
                                })
                              }}
                              placeholder="0"
                              min={0}
                            />
                          </div>
                          <div className="col-4">
                            <label className="form-label">מ"ר</label>
                            <input
                              type="number"
                              className="form-control"
                              value={editingPendingListing.home_data?.square_meters || ''}
                              onChange={(e) => setEditingPendingListing({
                                ...editingPendingListing,
                                home_data: { ...editingPendingListing.home_data, square_meters: parseInt(e.target.value) || 0 }
                              })}
                              placeholder="0"
                              min={0}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEditPendingModal(false)
                    setEditingPendingListing(null)
                    const params = new URLSearchParams(searchParams)
                    params.delete('pending')
                    setSearchParams(params, { replace: true })
                  }}
                >
                  ביטול
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={retryingId === editingPendingListing._id || (
                    (editingPendingListing.listing_type === 'home_sale' || editingPendingListing.listing_type === 'home_rent' || editingPendingListing.listing_type === 'rental') &&
                    !editingPendingListing.home_data?.bedrooms
                  )}
                  title={
                    (editingPendingListing.listing_type === 'home_sale' || editingPendingListing.listing_type === 'home_rent' || editingPendingListing.listing_type === 'rental') &&
                    !editingPendingListing.home_data?.bedrooms
                      ? 'יש למלא מספר חדרים לפני שמירה'
                      : undefined
                  }
                  onClick={async () => {
                    // Validate rooms required for property listings
                    const isProperty = editingPendingListing.listing_type === 'home_sale' || editingPendingListing.listing_type === 'home_rent' || editingPendingListing.listing_type === 'rental'
                    if (isProperty && !editingPendingListing.home_data?.bedrooms) {
                      alert('חובה למלא מספר חדרים')
                      return
                    }
                    // Validate photos
                    const needsPhotoError = !editingPendingListing.photo_urls || editingPendingListing.photo_urls.length === 0
                      ? 'חובה להוסיף לפחות תמונה אחת'
                      : editingPendingListing.photo_urls.length > 0 && /\.(mp4|mov|avi|webm|mkv)(\?|$)/i.test(editingPendingListing.photo_urls[0])
                        ? 'התמונה הראשונה חייבת להיות תמונה, לא סרטון'
                        : null
                    if (needsPhotoError) {
                      const photoSection = document.getElementById('pending-photo-section')
                      if (photoSection) {
                        photoSection.scrollIntoView({ behavior: 'smooth', block: 'center' })
                        photoSection.style.outline = '2px solid #dc3545'
                        photoSection.style.borderRadius = '8px'
                        photoSection.style.padding = '8px'
                        // Show error message
                        let errEl = document.getElementById('pending-photo-error')
                        if (!errEl) {
                          errEl = document.createElement('div')
                          errEl.id = 'pending-photo-error'
                          errEl.style.cssText = 'color:#dc3545;font-size:13px;font-weight:600;margin-top:4px'
                          photoSection.appendChild(errEl)
                        }
                        errEl.textContent = needsPhotoError
                        setTimeout(() => {
                          photoSection.style.outline = ''
                          photoSection.style.padding = ''
                          if (errEl) errEl.remove()
                        }, 3000)
                      }
                      return
                    }
                    try {
                      // Save changes first
                      const authToken = localStorage.getItem('kt-auth-react-v')
                      const parsed = authToken ? JSON.parse(authToken) : null
                      if (parsed?.api_token) {
                        await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/my-listings/${editingPendingListing._id}`, {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${parsed.api_token}`
                          },
                          body: JSON.stringify({
                            title: editingPendingListing.title,
                            description: editingPendingListing.description,
                            price: editingPendingListing.price,
                            address: editingPendingListing.address,
                            latitude: editingPendingListing.latitude,
                            longitude: editingPendingListing.longitude,
                            item_attributes: editingPendingListing.listing_type === 'item' ? editingPendingListing.item_attributes : undefined,
                            vehicle_data: editingPendingListing.listing_type === 'vehicle' ? editingPendingListing.vehicle_data : undefined,
                            home_data: (editingPendingListing.listing_type === 'home_sale' || editingPendingListing.listing_type === 'home_rent' || editingPendingListing.listing_type === 'rental') ? editingPendingListing.home_data : undefined,
                            category_id: editingPendingListing.category_id,
                            category_name: editingPendingListing.category_name,
                            profile_ids: editingPendingListing.profile_ids,
                            profile_names: editingPendingListing.profile_names,
                            listing_type: editingPendingListing.listing_type,
                            photo_urls: editingPendingListing.photo_urls
                          })
                        })
                      }
                      // Then retry
                      await handleRetry(editingPendingListing._id)
                      setShowEditPendingModal(false)
                      setEditingPendingListing(null)
                      const params = new URLSearchParams(searchParams)
                      params.delete('pending')
                      setSearchParams(params, { replace: true })
                    } catch (err) {
                      console.error('Failed to save and retry:', err)
                    }
                  }}
                >
                  {retryingId === editingPendingListing._id ? (
                    <>
                      <i className="bi bi-arrow-clockwise spin me-2"></i>
                      שומר ומפרסם...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-2"></i>
                      שמור ופרסם מחדש
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={async () => {
                    await handleDeleteMyListing(editingPendingListing._id)
                    setShowEditPendingModal(false)
                    setEditingPendingListing(null)
                    const params = new URLSearchParams(searchParams)
                    params.delete('pending')
                    setSearchParams(params, { replace: true })
                  }}
                >
                  <i className="bi bi-trash me-2"></i>
                  מחק
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
